/**
 * engine/loop.ts — The Game Loop
 *
 * This is the pulse. One requestAnimationFrame loop, ticking forever,
 * orchestrating every system in the right order so that nothing steps on
 * anything else. Input is read, state is updated, the world is rendered —
 * frame after frame, for as long as you're here.
 *
 * System update order:
 *   1. Input    — flush events into clean InputState
 *   2. State    — camera, time, transitions
 *   3. Room     — furniture animations, plant growth, pet wandering
 *   4. Characters — movement, animation, collision
 *   5. Render   — clear, depth sort, draw
 *
 * Tab visibility: time does not freeze when you look away. The clock
 * keeps ticking. Plants keep growing (slowly). The room is still there,
 * quietly being itself. But we cap dt so we don't get a 30-second
 * physics explosion when you switch back.
 */

import { GameState, FurniturePlacement, updateCamera, updateTime, beginTransition, createStarterRoom, createTestZone, asCamera, ZOOM_MIN, ZOOM_MAX } from "./state.js";
import { updatePresence } from "../aeterna/presence.js";
import { AeternaConductor } from "../aeterna/conductor.js";
import { InputSystem, type InputState } from "./input.js";
import { TitleRenderer } from "../render/title.js";
import { RoomRenderer } from "../render/room.js";
import { CharacterCreateRenderer } from "../render/character_create.js";
import { AudioEngine } from "../audio/engine.js";
import { ParticleSystem } from "../particles/ambient.js";
import { InteractionSystem } from "../interact/system.js";
import { getFootprintTiles, getFurnitureType } from "../room/furniture.js";
import { screenToTile, worldToScreen, applyCamera } from "../iso/projection.js";
import type { Camera } from "../iso/projection.js";
import { updateCreature, cleanupCreature, type CreatureUpdateContext } from "../creature/update.js";
import { cleanupCreatureAttention } from "../creature/renderer.js";
import { createCreature } from "../creature/state.js";
// Side-effect imports: register all creature species templates
import "../creature/templates/cat.js";
import "../creature/templates/dog.js";
import "../creature/templates/wolf.js";
import "../creature/templates/horse.js";
import "../creature/templates/pony.js";
import "../creature/templates/fantasy.js";
import "../creature/templates/training_dummy.js";
import { DeviceOverlay } from "../device/overlay.js";
import { setDeviceContext } from "../character/composer.js";
import { haptic } from "./mobile.js";
// Side-effect import: registers all device apps into the app registry
import "../device/apps/index.js";
import { TransitionRenderer } from "../render/transition.js";
import { updateTravel, getZone } from "../world/zones.js";
import { generateZoneRoom, getZoneCreatures } from "../world/room_gen.js";
import { updateCombat, scanForAggro, startCombat, clearCreatureCombatHp, getCreatureHp, regenHp, setCombatAudioEngine } from "../combat/system.js";
import { getZoneNpcs } from "../npc/npcs.js";
import { drawCombatUI } from "../render/combat_ui.js";
import { drawSkillBar, drawEmoteWheel, drawFloatingEmotes } from "../render/ability_ui.js";
import { tickBuffs, clearAllBuffs, setBuffAudioEngine } from "../combat/buffs.js";
import { triggerEmote, openEmoteWheel, closeEmoteWheel, cleanupFloatingEmotes, EMOTES } from "../combat/emotes.js";
import { getAbility } from "../combat/skills.js";
import { executeAbility, assignStarterAbilities, updateWeaponAbilities } from "../combat/ability_engine.js";
import { findPath } from "../pathfinding/astar.js";
import { addToast } from "../render/toasts.js";
import { drawNameplatesAndStatus, addCombatLogEntry } from "../render/nameplate_ui.js";
import {
  togglePvP, updatePvP, updateDeath, updateTarget, killPlayer, respawnPlayer, targetCreature,
  targetNpc, updateDuel, challengeToDuel, acceptDuel, declineDuel, forfeitDuel,
  openCreatureContextMenu, openNpcContextMenu, openPlayerContextMenu, closeContextMenu,
} from "../combat/pvp.js";
import { openDialog, handleDialogOption, handleFreeTextInput, closeDialog, updateQuestMarks, checkQuestTriggers } from "../narrative/dialog.js";
import { drawDialogPanel, drawQuestTracker, drawQuestLog, drawQuestOverlay, drawDPSMeter, dialogHitZones, dialogInputHitZone, dialogPanelBounds } from "../render/quest_ui.js";
import { isTrainingDummy } from "../combat/system.js";

/** Push a message to the ARIA live region so screen readers announce it */
function announceToScreenReader(text: string): void {
  const el = document.getElementById("aria-live");
  if (!el) return;
  const span = document.createElement("span");
  span.textContent = text;
  el.appendChild(span);
  // Clean up old announcements to prevent DOM bloat
  while (el.childNodes.length > 20) {
    el.removeChild(el.firstChild!);
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum dt in seconds — caps the spike when returning from a background tab */
const MAX_DT = 0.1;

/** Compose bar height in the Hearth device app (must match hearth.ts COMPOSE_BAR_H) */
const COMPOSE_BAR_H = 32;

/** Number of frames in the rolling average for performance tracking */
const PERF_WINDOW = 60;

/** Adaptive quality threshold: drop quality when avg frame time exceeds this */
const QUALITY_DROP_THRESHOLD_MS = 18;  // ~55fps

/** Restore quality when avg frame time is below this */
const QUALITY_RESTORE_THRESHOLD_MS = 14; // ~71fps

// ─── Performance Tracker ─────────────────────────────────────────────────────

export interface PerformanceMetrics {
  /** Rolling average of frame render time in milliseconds */
  avgFrameTimeMs: number;
  /** Current quality level: 1.0 = full, 0.5 = reduced effects */
  qualityLevel: number;
  /** Frames per second (derived from avgFrameTimeMs) */
  fps: number;
  /** Total frames rendered */
  frameCount: number;
}

class PerformanceTracker {
  private times: Float64Array;
  private index = 0;
  private count = 0;
  public qualityLevel = 1.0;
  public frameCount = 0;

  constructor() {
    this.times = new Float64Array(PERF_WINDOW);
  }

  record(frameTimeMs: number): void {
    this.times[this.index] = frameTimeMs;
    this.index = (this.index + 1) % PERF_WINDOW;
    if (this.count < PERF_WINDOW) this.count++;
    this.frameCount++;
  }

  get avgFrameTimeMs(): number {
    if (this.count === 0) return 16.6;
    let sum = 0;
    for (let i = 0; i < this.count; i++) sum += this.times[i];
    return sum / this.count;
  }

  get fps(): number {
    const avg = this.avgFrameTimeMs;
    return avg > 0 ? 1000 / avg : 60;
  }

  /**
   * Adaptively adjust quality based on frame timing.
   * Drops effects when struggling, restores when smooth again.
   * Hysteresis prevents flickering at the threshold.
   */
  adaptQuality(): void {
    const avg = this.avgFrameTimeMs;
    if (avg > QUALITY_DROP_THRESHOLD_MS && this.qualityLevel > 0.5) {
      // Struggling — reduce quality in steps
      this.qualityLevel = Math.max(0.5, this.qualityLevel - 0.1);
    } else if (avg < QUALITY_RESTORE_THRESHOLD_MS && this.qualityLevel < 1.0) {
      // Running well — gradually restore quality
      this.qualityLevel = Math.min(1.0, this.qualityLevel + 0.05);
    }
  }

  toMetrics(): PerformanceMetrics {
    return {
      avgFrameTimeMs: this.avgFrameTimeMs,
      qualityLevel: this.qualityLevel,
      fps: this.fps,
      frameCount: this.frameCount,
    };
  }
}

// ─── Game Loop ────────────────────────────────────────────────────────────────

export class GameLoop {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private state: GameState;
  private input: InputSystem;
  private perf: PerformanceTracker;

  private rafId: number = 0;
  private lastTimestamp: number = 0;
  private running = false;

  // Renderers — created lazily when their scene first appears
  private titleRenderer: TitleRenderer | null = null;
  private roomRenderer: RoomRenderer | null = null;
  private charCreateRenderer: CharacterCreateRenderer | null = null;

  // New systems — lazily created on first room entry
  private audioEngine: AudioEngine | null = null;
  private particleSystem: ParticleSystem | null = null;
  private interactionSystem: InteractionSystem | null = null;

  // Device overlay — always available (device works in any scene)
  private deviceOverlay: DeviceOverlay = new DeviceOverlay();

  // Zone travel cinematic — renders the full-screen transition between zones
  private transitionRenderer: TransitionRenderer = new TransitionRenderer();

  // Interaction animation timer — auto-resets to idle after duration
  private interactTimer: number = 0;
  private static readonly INTERACT_DURATION = 1.0; // seconds

  // Smooth turning — step through intermediate compass directions instead of
  // instant snap. A natural human turn pivots through each ~45° increment;
  // anything faster looks like a glitch, not a body in motion.
  private turnTargetDir: import("./state.js").Direction = "S";
  private turnStepTimer: number = 0;
  private static readonly TURN_STEP_TIME = 0.065; // seconds per 45° compass step
  private static readonly DIR_RING: import("./state.js").Direction[] =
    ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  // Post-turn ease-in: after a multi-step turn completes, ramp speed from
  // 0 → 1 over this duration so the character doesn't snap to full walk speed.
  private postTurnAccel: number = 1;        // 0→1 ramp factor
  private wasTurning: boolean = false;
  private static readonly POST_TURN_ACCEL_TIME = 0.18; // seconds to reach full speed

  // WASD continuous movement — tracks keyboard-driven motion for tile chaining.
  // When wasdMoving is true, the tile-arrival handler queues the next tile in
  // the held direction instead of returning to idle.
  private wasdMoving: boolean = false;
  private wasdTileDX: number = 0;  // current isometric tile delta X (-1/0/+1)
  private wasdTileDY: number = 0;  // current isometric tile delta Y (-1/0/+1)

  // Combat audio state tracking
  private _wasCombatActive: boolean = false;
  private _lastHeartbeatTime: number = 0;

  constructor(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    state: GameState,
    input: InputSystem,
    audioEngine?: AudioEngine
  ) {
    this.canvas = canvas;
    this.ctx = ctx;
    this.state = state;
    this.input = input;
    this.perf = new PerformanceTracker();
    this.audioEngine = audioEngine ?? null;
    if (this.audioEngine) {
      setCombatAudioEngine(this.audioEngine);
      setBuffAudioEngine(this.audioEngine);
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTimestamp = performance.now();
    this.rafId = requestAnimationFrame(this.tick.bind(this));
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  // ─── The Tick ─────────────────────────────────────────────────────────────

  private tick(timestamp: number): void {
    if (!this.running) return;

    const frameStart = performance.now();

    // ── Delta time ──────────────────────────────────────────────────────────
    const rawDt = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;

    // Cap dt: returning from a background tab could give 30s. We cap at 0.1s
    // and let the game clock catch up gracefully rather than explode.
    const dt = Math.min(rawDt, MAX_DT);

    // ── Tab visibility compensation ─────────────────────────────────────────
    // The tab has been visible this frame (we're running). The state tracks
    // whether we *were* hidden last frame so we can log it or adjust.
    const wasHidden = !this.state.tabVisible;
    this.state.tabVisible = true;

    if (wasHidden) {
      // We just came back from being hidden. The room is still there,
      // but we don't want to simulate 30 seconds of physics.
      // Time has passed — the clock updates with dt which is already capped.
      // This is intentional: if you were gone for a minute, the room advanced
      // a few game-hours, but no more.
    }

    // ══════════════════════════════════════════════════════════════════════
    // 1. INPUT — flush events, compute tile positions
    // ══════════════════════════════════════════════════════════════════════
    this.input.update(this.state);
    const inputState = this.input.state;

    // ══════════════════════════════════════════════════════════════════════
    // 2. STATE — time, camera, transitions, UI
    // ══════════════════════════════════════════════════════════════════════

    // Time always passes, tab or not (dt is capped above)
    updateTime(this.state, dt);

    // Camera follows player when in room
    if (this.state.scene === "room" && this.state.currentRoom) {
      const player = this.state.player;
      // Smoothly center camera on player tile
      const nextStep = player.movementQueue?.[0];
      const tx = player.position.tileX + (player.moveProgress > 0 && nextStep
        ? (nextStep.tileX - player.position.tileX) * player.moveProgress
        : 0);
      const ty = player.position.tileY + (player.moveProgress > 0 && nextStep
        ? (nextStep.tileY - player.position.tileY) * player.moveProgress
        : 0);

      const wx = tx + 0.5;
      const wy = ty + 0.5;
      const screenX = (wx - wy) * 32;
      const screenY = (wx + wy) * 16;
      this.state.camera.targetX = this.state.canvasWidth / 2 - screenX * this.state.camera.zoom;
      this.state.camera.targetY = this.state.canvasHeight / 2 - screenY * this.state.camera.zoom;
    }

    // Apply zoom from scroll/pinch (only in room scene)
    // When inventory is open, scroll wheel pages through inventory instead of zooming
    if (inputState.wheelZoomDelta !== 0 && this.state.ui.inventoryOpen && this.roomRenderer) {
      this.roomRenderer.hud.scrollInventory(inputState.wheelZoomDelta > 0 ? -1 : 1, this.state);
    } else if (inputState.wheelZoomDelta !== 0 && this.state.ui.deviceOpen && this.state.player.device.activeApp) {
      this.deviceOverlay.handleWheel(inputState.wheelZoomDelta, this.state);
    } else if (this.state.scene === "room" && !this.state.ui.deviceOpen && (inputState.wheelZoomDelta !== 0 || inputState.pinchZoomDelta !== 0)) {
      const delta = inputState.wheelZoomDelta + inputState.pinchZoomDelta;
      const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.state.camera.targetZoom + delta));
      this.state.camera.targetZoom = newZoom;
    }

    // ── Edge scrolling for large maps ─────────────────────────────────────
    // When the mouse is within EDGE_SCROLL_MARGIN pixels of any screen edge
    // AND the room is bigger than the viewport, pan the camera in that direction.
    // Suppressed during dialog/quest log to prevent accidental camera drift.
    if (this.state.scene === "room" && this.state.currentRoom && !this.state.dialog.open && !this.state.questUI.logOpen && !this.state.ui.mapOpen) {
      const EDGE_SCROLL_MARGIN = 30;
      const EDGE_SCROLL_SPEED = 350; // pixels per second
      const cw = this.state.canvasWidth;
      const ch = this.state.canvasHeight;
      const mx = inputState.pointer.x;
      const my = inputState.pointer.y;
      const room = this.state.currentRoom;
      const zoom = this.state.camera.zoom;

      // Only activate if the room is bigger than the viewport (in either dimension)
      const roomPixelW = (room.width + room.height) * 32 * zoom;
      const roomPixelH = (room.width + room.height) * 16 * zoom;
      const isLargeMap = roomPixelW > cw * 0.9 || roomPixelH > ch * 0.9;

      if (isLargeMap && mx >= 0 && mx <= cw && my >= 0 && my <= ch) {
        let edgeDX = 0;
        let edgeDY = 0;

        if (mx < EDGE_SCROLL_MARGIN) edgeDX = EDGE_SCROLL_SPEED * dt;
        else if (mx > cw - EDGE_SCROLL_MARGIN) edgeDX = -EDGE_SCROLL_SPEED * dt;

        if (my < EDGE_SCROLL_MARGIN) edgeDY = EDGE_SCROLL_SPEED * dt;
        else if (my > ch - EDGE_SCROLL_MARGIN) edgeDY = -EDGE_SCROLL_SPEED * dt;

        if (edgeDX !== 0 || edgeDY !== 0) {
          this.state.camera.targetX += edgeDX;
          this.state.camera.targetY += edgeDY;
        }
      }
    }

    updateCamera(this.state, dt);

    // Hover tile → update UI (TilePoint {tx,ty} → UIState {x,y})
    this.state.ui.hoveredTile = inputState.hoveredTile
      ? { x: inputState.hoveredTile.tx, y: inputState.hoveredTile.ty }
      : null;

    // Suppress chat on non-room scenes
    this.input.chatEnabled = this.state.scene === "room";

    // Reset per-frame UI flag before any click processing
    this.state.ui.uiClickConsumed = false;

    // Context menu hover tracking — update hoverIdx each frame
    if (this.state.contextMenu.open) {
      const cm = this.state.contextMenu;
      const px = inputState.pointer.x;
      const py = inputState.pointer.y;
      const optH = 22;
      const padY = 6;
      const relY = py - cm.y - padY;
      const idx = Math.floor(relY / optH);
      cm.hoverIdx = (idx >= 0 && idx < cm.options.length && px >= cm.x && px <= cm.x + 160) ? idx : -1;
    }

    // Context menu click — handle option selection or close
    if (this.state.contextMenu.open && inputState.clickPosition) {
      const cm = this.state.contextMenu;
      const cx = inputState.clickPosition.x;
      const cy = inputState.clickPosition.y;
      // Check if click is inside the menu area (rough bounds)
      const optH = 24;
      const menuH = cm.options.length * optH + 8;
      const menuW = 140;
      if (cx >= cm.x && cx <= cm.x + menuW && cy >= cm.y && cy <= cm.y + menuH) {
        // Determine which option was clicked
        const optIdx = Math.floor((cy - cm.y - 4) / optH);
        if (optIdx >= 0 && optIdx < cm.options.length) {
          const opt = cm.options[optIdx];
          if (opt.enabled) {
            this.handleContextMenuAction(opt.id, cm.targetId, cm.targetType ?? "creature");
          }
        }
      }
      closeContextMenu(this.state);
      this.state.ui.uiClickConsumed = true;
    }

    // Emote wheel click — fire selected emote on click while wheel is open
    if (this.state.player.emoteWheel.open && inputState.clickPosition) {
      const sel = this.state.player.emoteWheel.selectedIndex;
      if (sel >= 0 && sel < EMOTES.length) {
        triggerEmote(this.state, EMOTES[sel].id);
      } else {
        closeEmoteWheel(this.state);
      }
      this.state.ui.uiClickConsumed = true;
    }

    // Device overlay click interception — consumes clicks before HUD/movement
    if (this.state.ui.deviceOpen && inputState.clickPosition) {
      const wasOpen = this.state.ui.deviceOpen;
      if (this.deviceOverlay.handleTap(inputState.clickPosition.x, inputState.clickPosition.y, this.state)) {
        this.state.ui.uiClickConsumed = true;
        if (!this.state.ui.deviceOpen) {
          // Device was closed by tap (tapped outside)
          this.state.player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";
          if (this.audioEngine) this.audioEngine.playInteraction("device_close");
        } else if (wasOpen) {
          // Tapped inside device — play tap sound
          if (this.audioEngine) this.audioEngine.playInteraction("device_tap");
        }
      }
    }

    // ── Zone travel detection ───────────────────────────────────────────
    // When an app (Atlas) triggers travel, detect it and start the visual transition
    if (this.state.world.travelState.active && this.state.scene === "room") {
      const ts = this.state.world.travelState;
      const fromZone = getZone(this.state.world, ts.fromZoneId ?? "");
      const toZone = getZone(this.state.world, ts.toZoneId ?? "");

      if (fromZone && toZone) {
        // Save the player's personal room when leaving home
        if (fromZone.id === "ember_lane" && this.state.currentRoom && this.state.currentRoom.ownerId === this.state.player.id) {
          this.savedPlayerRoom = this.state.currentRoom;
        }

        // Close device overlay
        if (this.state.ui.deviceOpen) {
          this.deviceOverlay.close(this.state);
          this.state.player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";
        }

        // Start cinematic transition
        this.transitionRenderer.start({
          fromZoneName: fromZone.name,
          toZoneName: toZone.name,
          toZoneDescription: toZone.description,
          toZoneColor: toZone.primaryColor,
          toZoneDangerLevel: toZone.dangerLevel,
          travelDuration: ts.duration,
          fromZoneColor: fromZone.primaryColor,
        });

        // Switch to zone_travel scene
        this.state.previousScene = "room";
        this.state.scene = "zone_travel";
      }
    }

    // Route raw canvas clicks through HUD before movement or placement
    if (this.state.scene === "room" && inputState.clickPosition && this.roomRenderer && !this.state.ui.uiClickConsumed) {
      const { x, y } = inputState.clickPosition;
      if (this.roomRenderer.handleUIClick(x, y, this.state)) {
        this.state.ui.uiClickConsumed = true;
      }
    }

    // Smart HUD click-throughs — clicking combat panel / currency opens relevant device app
    if (this.state.scene === "room" && inputState.clickPosition && !this.state.ui.uiClickConsumed
        && !this.state.ui.deviceOpen && !this.state.chat.inputOpen && !this.state.player.combatState.active) {
      const clickX = inputState.clickPosition.x;
      const clickY = inputState.clickPosition.y;
      // Combat health/XP panel area (matches combat_ui.ts layout constants)
      const CP_X = 16, CP_Y = 108, CP_W = 200, CP_H = 72;
      if (clickX >= CP_X && clickX <= CP_X + CP_W && clickY >= CP_Y && clickY <= CP_Y + CP_H + 30) {
        this.state.ui.deviceToggleRequest = "phone";
        this.state.ui.deviceOpenToApp = "pulse";
        this.state.ui.uiClickConsumed = true;
      }
      // Currency display in the action bar — opens catalog/shop
      // Currency badge is at barX+58, midY+5 in the bottom action bar (see hud.ts drawActionBar)
      if (!this.state.ui.uiClickConsumed) {
        const barY = this.state.canvasHeight - 72 - 10;
        const cX = 12 + 58;  // BAR_PAD_X + currency offset
        const cY = barY + 36 + 5;  // barY + BAR_H/2 + 5
        if (clickX >= cX && clickX <= cX + 60 && clickY >= cY - 10 && clickY <= cY + 10) {
          this.state.ui.deviceToggleRequest = "phone";
          this.state.ui.deviceOpenToApp = "catalog";
          this.state.ui.uiClickConsumed = true;
        }
      }
    }

    // Right-click → open furniture context menu for whatever is on that tile
    if (this.state.scene === "room" && inputState.contextPosition && !this.state.chat.inputOpen && !this.state.ui.deviceOpen) {
      this.handleContextClick(inputState.contextPosition.x, inputState.contextPosition.y);
      this.state.ui.uiClickConsumed = true;
    }

    // Handle keyboard shortcuts
    this.handleGlobalKeys(inputState);

    // Character creation update
    if (this.state.scene === "character_create") {
      this.updateCharacterCreate(dt, inputState);
    }

    // Scene transitions
    this.updateTransition(dt, inputState);

    // Update zone travel
    if (this.state.scene === "zone_travel") {
      // Advance travel state machine (uses Date.now() internally)
      const result = updateTravel(this.state.world, dt);

      // Advance visual transition (uses ms)
      const transitionDone = this.transitionRenderer.update(dt * 1000);

      // Need BOTH the visual transition AND travel state machine to complete
      const arrived = result.arrived || !this.state.world.travelState.active;
      if (arrived && transitionDone) {
        const zoneId = result.zoneId ?? this.state.world.currentZoneId;
        this.enterZone(zoneId);
      } else if (arrived && !transitionDone) {
        // Travel finished but visual transition still playing — let it finish naturally
      } else if (!arrived && transitionDone) {
        // Visual transition done but travel still going — force-complete travel
        // This happens for long travel durations (danger zones). Skip to arrival.
        const ts = this.state.world.travelState;
        if (ts.active && ts.toZoneId) {
          const arrivedZoneId = ts.toZoneId;
          const arrivedZone = getZone(this.state.world, arrivedZoneId);
          if (arrivedZone) {
            arrivedZone.waystoneDiscovered = true;
            if (arrivedZone.explored === 0) arrivedZone.explored = 0.1;
          }
          this.state.world.currentZoneId = arrivedZoneId;
          ts.active = false;
          ts.progress = 0;
          ts.fromZoneId = null;
          ts.toZoneId = null;
          this.enterZone(arrivedZoneId);
        }
      }
    }

    // Title/transition audio — keep fire crackles going before room loads
    if (this.state.scene !== "room" && this.audioEngine) {
      this.audioEngine.tickTitleAudio();
    }

    // ══════════════════════════════════════════════════════════════════════
    // 3. ROOM — furniture animations, ambient effects
    // ══════════════════════════════════════════════════════════════════════
    if (this.state.scene === "room" && this.state.currentRoom) {
      this.updateRoom(dt);
    }

    // ── Creature update ─────────────────────────────────────────────────
    if (this.state.scene === "room" && this.state.currentRoom) {
      this.updateCreatures(dt);
    }

    // ── Combat update ───────────────────────────────────────────────────
    if (this.state.scene === "room") {
      // Scan for hostile creatures aggro
      const zoneId = this.state.world?.currentZoneId;
      if (zoneId) {
        const zone = getZone(this.state.world, zoneId);
        if (zone && zone.dangerLevel > 0) {
          scanForAggro(this.state, zone.dangerLevel);
        }
      }
      // Update active combat
      const combatImpacts = updateCombat(this.state, dt);
      // Emit impact particles at hit locations
      if (this.particleSystem && combatImpacts.length > 0) {
        const cam = { x: this.state.camera.x, y: this.state.camera.y, zoom: this.state.camera.zoom };
        for (const impact of combatImpacts) {
          const ws = worldToScreen(impact.wx, impact.wy, impact.wz);
          const sc = applyCamera(ws, cam);
          this.particleSystem.emitImpact(sc.x, sc.y, impact.isCrit);
        }
      }
      // Regenerate HP when out of combat
      regenHp(this.state, dt);

      // Tick buffs — process DoTs, HoTs, expiry
      const buffResult = tickBuffs(this.state, dt);
      // Spawn damage numbers for DoT/HoT ticks
      if (buffResult.ticks.length > 0) {
        const px = this.state.player.position.tileX;
        const py = this.state.player.position.tileY;
        for (const tick of buffResult.ticks) {
          const sx = (px - py) * 32 + (Math.random() - 0.5) * 16;
          const sy = (px + py) * 16 - 40 + (Math.random() - 0.5) * 8;
          this.state.player.combatState.damageNumbers.push({
            value: tick.value,
            x: sx, y: sy,
            createdAt: performance.now() / 1000,
            isPlayerDamage: !tick.isHeal,
            isCrit: false,
            damageType: tick.damageType,
            angle: tick.isHeal ? -Math.PI / 2 : -Math.PI / 2 + (Math.random() - 0.5) * 0.8,
          });
        }
      }

      // Regenerate Ember Points
      const epInCombat = this.state.player.combatState.active;
      const epRate = epInCombat ? this.state.player.combat.ember.combatRegenRate : this.state.player.combat.ember.regenRate;
      this.state.player.combat.ember.current = Math.min(
        this.state.player.combat.ember.max,
        this.state.player.combat.ember.current + epRate * dt,
      );
    }

    // Clean up floating emotes
    cleanupFloatingEmotes(this.state);

    // Update quest giver marks for all NPCs in the current zone
    if (this.state.npcs.length > 0) {
      updateQuestMarks(this.state);
    }

    // Check quest auto-completion triggers (zone entry, combat events, etc.)
    if (this.state.player.quests.length > 0) {
      checkQuestTriggers(this.state);
    }

    // Arena ambient particles — sand wisps, torch sparks in training grounds
    {
      const zone = getZone(this.state.world, this.state.world.currentZoneId);
      if (zone && zone.type === "training" && this.state.currentRoom) {
        const particles = this.state.arenaParticles;
        const cw = this.state.canvasWidth;
        const ch = this.state.canvasHeight;

        // Spawn new particles (cap at 40)
        if (particles.length < 40 && Math.random() < 0.15) {
          const type = Math.random() < 0.6 ? "sand" as const : Math.random() < 0.5 ? "spark" as const : "dust" as const;
          const p = {
            x: Math.random() * cw,
            y: type === "spark" ? ch * 0.6 + Math.random() * ch * 0.3 : Math.random() * ch,
            vx: (Math.random() - 0.3) * 20,
            vy: type === "spark" ? -(10 + Math.random() * 25) : (Math.random() - 0.5) * 6,
            life: type === "spark" ? 0.5 + Math.random() * 0.8 : 2 + Math.random() * 4,
            maxLife: 0,
            size: type === "spark" ? 1 + Math.random() : type === "sand" ? 0.8 + Math.random() * 0.6 : 1 + Math.random() * 1.5,
            color: type === "spark" ? "#ffa030" : type === "sand" ? "#c8a868" : "#a09070",
            type,
          };
          p.maxLife = p.life;
          particles.push(p);
        }

        // Update existing particles
        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.life -= dt;
          if (p.type === "spark") p.vy += 30 * dt; // gravity
          if (p.life <= 0 || p.x < -10 || p.x > cw + 10 || p.y < -10 || p.y > ch + 10) {
            particles.splice(i, 1);
          }
        }
      } else {
        // Clear particles when not in training zone
        if (this.state.arenaParticles.length > 0) {
          this.state.arenaParticles.length = 0;
        }
      }
    }

    // Expire emote animation — revert to idle when the animation duration ends
    {
      const now = performance.now() / 1000;
      if (this.state.player.activeEmoteId && now >= this.state.player.activeEmoteUntil) {
        this.state.player.activeEmoteId = null;
        this.state.player.activeEmoteUntil = 0;
        // Only revert if still in an emote-related state (not if player started walking)
        const a = this.state.player.animState;
        if (a === "emote" || a === "wave" || a === "dance" || a === "sleep") {
          this.state.player.animState = this.state.ui.combatStance ? "combat_idle" : "idle";
        }
      }
    }

    // Update buff aura color — pick the most visually dominant active buff
    {
      const buffs = this.state.player.activeBuffs;
      if (buffs.length > 0) {
        const priorityMap: Record<string, number> = {
          shield: 6, regen: 5, might: 4, haste: 3, fortify: 2, thorns: 1,
        };
        const colorMap: Record<string, string> = {
          shield: "#AADDFF", regen: "#88FF88", might: "#FF8866",
          haste: "#FFEE88", fortify: "#AAAACC", thorns: "#66DD44",
        };
        let bestColor: string | null = null;
        let bestPri = -1;
        for (const b of buffs) {
          if (b.isDebuff) continue;
          const p = priorityMap[b.type] ?? 0;
          if (p > bestPri) { bestPri = p; bestColor = colorMap[b.type] ?? null; }
        }
        this.state.player.buffAuraColor = bestColor;
        this.state.player.buffAuraIntensity = bestColor ? Math.min(1, 0.3 + buffs.filter(b => !b.isDebuff).length * 0.15) : 0;
      } else {
        this.state.player.buffAuraColor = null;
        this.state.player.buffAuraIntensity = 0;
      }
    }

    // Update PvP timers (aggressor expiry)
    updatePvP(this.state, dt);

    // Update duel countdown and win detection
    {
      const duel = this.state.player.duel;
      const prevCountdown = duel.countdown;
      const prevWinner = duel.winnerId;
      updateDuel(this.state, dt);

      // Duel audio — countdown drum ticks
      if (duel.active && duel.countdown > 0 && this.audioEngine) {
        const tick = Math.ceil(duel.countdown);
        if (tick !== duel.lastCountdownTick && tick > 0 && tick <= 3) {
          this.audioEngine.playInteraction("duel_countdown");
          duel.lastCountdownTick = tick;
        }
      }
      // Duel audio — FIGHT horn when countdown hits 0
      if (prevCountdown > 0 && duel.countdown <= 0 && duel.active && !duel.winnerId && this.audioEngine) {
        this.audioEngine.playInteraction("duel_fight");
      }
      // Duel audio — victory/defeat fanfare
      if (!prevWinner && duel.winnerId && this.audioEngine) {
        const won = duel.winnerId !== this.state.player.id;
        this.audioEngine.playInteraction(won ? "duel_victory" : "duel_defeat");
      }
    }

    // Update death/respawn (countdown, ghost alpha pulsing)
    updateDeath(this.state, dt);

    // Update target frame (distance tracking, despawn detection)
    updateTarget(this.state);

    // Update focus target HP (live tracking)
    {
      const ft = this.state.player.focusTarget;
      if (ft.id) {
        if (ft.type === "creature") {
          const hp = getCreatureHp(ft.id);
          if (hp) {
            ft.hp = hp.hp;
            ft.maxHp = hp.maxHp;
          } else {
            // Creature despawned — clear focus
            ft.id = null;
            ft.type = null;
            ft.name = null;
          }
        } else if (ft.type === "player") {
          const p = this.state.otherPlayers.find(pl => pl.id === ft.id);
          if (p) {
            ft.hp = p.combat.hp;
            ft.maxHp = p.combat.maxHp;
          }
        }
      }
    }

    // Check for player death — HP reaching 0
    if (this.state.player.combat.hp <= 0 && !this.state.player.death.isDead) {
      killPlayer(this.state, false);
      if (this.audioEngine) this.audioEngine.playInteraction("combat_death");
      addToast(this.state, "You have fallen...", "death");
      addCombatLogEntry("You have fallen...", "#e04040");
    }

    // Combat enter/exit audio stingers
    {
      const inCombat = this.state.player.combatState.active;
      if (inCombat && !this._wasCombatActive) {
        if (this.audioEngine) this.audioEngine.playInteraction("combat_enter");
      } else if (!inCombat && this._wasCombatActive) {
        if (this.audioEngine) this.audioEngine.playInteraction("combat_exit");
      }
      this._wasCombatActive = inCombat;
    }

    // Low-health heartbeat — visceral thump every 1.2s when HP < 30%
    {
      const hpRatio = this.state.player.combat.hp / Math.max(1, this.state.player.combat.maxHp);
      const now = performance.now() / 1000;
      if (hpRatio > 0 && hpRatio < 0.3 && !this.state.player.death.isDead) {
        if (now - this._lastHeartbeatTime >= 1.2) {
          if (this.audioEngine) this.audioEngine.playInteraction("heartbeat");
          this._lastHeartbeatTime = now;
        }
      } else {
        this._lastHeartbeatTime = 0;
      }
    }

    // Decay screen shake — done here in update(), not in render(),
    // to prevent double-decay during scene transitions.
    const shake = this.state.screenShake;
    if (shake.intensity > 0.1) {
      shake.intensity *= Math.exp(-shake.decay * dt);
      shake.phase += dt;  // advance phase for sin/cos oscillation
      if (shake.intensity < 0.1) shake.intensity = 0;
    }

    // ── Dying creature cleanup — remove finished death animations ──────
    {
      const nowSec = performance.now() / 1000;
      // Clean up attention/behavior state for dying creatures whose animation has finished
      for (const dc of this.state.dyingCreatures) {
        if (nowSec - dc.startTime >= dc.duration) {
          cleanupCreature(dc.creature.id);
          cleanupCreatureAttention(dc.creature.id);
        }
      }
      this.state.dyingCreatures = this.state.dyingCreatures.filter(
        dc => nowSec - dc.startTime < dc.duration,
      );
    }

    // ══════════════════════════════════════════════════════════════════════
    // 4. CHARACTERS — movement, collision, animation
    // ══════════════════════════════════════════════════════════════════════
    if (this.state.scene === "room") {
      this.updateCharacters(dt, inputState);
    }

    // ── Device overlay update ─────────────────────────────────────────────
    if (this.state.ui.deviceOpen || this.state.player.device.animPhase !== "stowed") {
      this.deviceOverlay.updatePointer(inputState.pointer.x, inputState.pointer.y, inputState.pointerDown);
      this.deviceOverlay.update(this.state, dt);
    }

    // ══════════════════════════════════════════════════════════════════════
    // 5. RENDER — draw everything
    // ══════════════════════════════════════════════════════════════════════
    this.render(timestamp);

    // ── Performance tracking ─────────────────────────────────────────────
    const frameTimeMs = performance.now() - frameStart;
    this.perf.record(frameTimeMs);
    // Only adapt every 60 frames to avoid thrashing
    if (this.perf.frameCount % 60 === 0) {
      this.perf.adaptQuality();
    }

    this.rafId = requestAnimationFrame(this.tick.bind(this));
  }

  // ─── Input Handling ───────────────────────────────────────────────────────

  private handleGlobalKeys(inputState: InputState): void {
    const { keysJustPressed } = this.input.state;
    const ui = this.state.ui;

    // Sync: device apps can set state.chat.inputOpen programmatically (e.g. Hearth compose bar)
    // Detect when inputOpen was set externally and open the DOM chat element to match
    if (this.state.chat.inputOpen && !this.input.state.chat.open) {
      // If aeterna mode, position input over the device compose bar
      if (this.state.chat.aeternaMode) {
        const contentRect = this.state.player.device.appContentRect;
        if (contentRect) {
          // Compute the compose bar position in screen space
          const composeRect = {
            x: contentRect.x,
            y: contentRect.y + contentRect.h - COMPOSE_BAR_H,
            w: contentRect.w,
            h: COMPOSE_BAR_H,
          };
          this.input.toggleChat(true, composeRect);
        } else {
          this.input.toggleChat(true);
        }
      } else {
        this.input.toggleChat(true);
      }
    }

    // Sync: device app edit mode — when an app sets editCallback, open the input
    const devEditCb = this.state.player.device.editCallback;
    if (devEditCb && !this.state.chat.inputOpen) {
      this.state.chat.inputOpen = true;
      const contentRect = this.state.player.device.appContentRect;
      if (contentRect) {
        const editRect = {
          x: contentRect.x,
          y: contentRect.y + contentRect.h - 28,
          w: contentRect.w,
          h: 28,
        };
        this.input.toggleChat(true, editRect);
      } else {
        this.input.toggleChat(true);
      }
      // Set placeholder and prefill value
      const chatEl = document.getElementById("chat-input") as HTMLInputElement | null;
      if (chatEl) {
        if (this.state.player.device.editPlaceholder) {
          chatEl.placeholder = this.state.player.device.editPlaceholder;
        }
        // Pre-fill with existing text if the editPrefill is set
        if (this.state.player.device.editPrefill) {
          chatEl.value = this.state.player.device.editPrefill;
          this.state.player.device.editPrefill = "";
        }
      }
    }

    // Enter → open chat
    if (keysJustPressed.has("Enter") && this.state.scene === "room") {
      if (!this.state.chat.inputOpen) {
        // Route to Aeterna mode when: device open on Hearth OR aeternaOpen toggle is active
        if ((ui.deviceOpen && this.state.player.device.activeApp === "hearth") || ui.aeternaOpen) {
          this.state.chat.aeternaMode = true;
          this.state.chat.inputOpen = true;
          // Will be picked up by the sync above next frame — or handle now
          const contentRect = this.state.player.device.appContentRect;
          if (contentRect) {
            const composeRect = {
              x: contentRect.x,
              y: contentRect.y + contentRect.h - COMPOSE_BAR_H,
              w: contentRect.w,
              h: COMPOSE_BAR_H,
            };
            this.input.toggleChat(true, composeRect);
          } else {
            this.input.toggleChat(true);
          }
        } else {
          this.state.chat.inputOpen = true;
          this.input.toggleChat(true);
        }
      }
    }

    // I → toggle inventory (room only)
    if (keysJustPressed.has("i") && this.state.scene === "room" && !this.state.chat.inputOpen) {
      ui.inventoryOpen = !ui.inventoryOpen;
      ui.uiSounds.push(ui.inventoryOpen ? "ui_open" : "ui_close");
      if (ui.inventoryOpen) { ui.aeternaOpen = false; if (ui.deviceOpen) this.deviceOverlay.close(this.state); }
    }

    // P → toggle build/edit mode (room only)
    // Track whether P was consumed by build toggle to prevent PvP from also firing
    let pConsumedByBuild = false;
    if (keysJustPressed.has("p") && this.state.scene === "room" && !this.state.chat.inputOpen) {
      if (ui.editMode) {
        ui.editMode = false;
        ui.selectedFurniture = null;
        ui.furnitureContextId = null;
        ui.placingFurnitureType = null;
        ui.movingFurnitureId = null;
        ui.inventoryOpen = false;
        ui.uiSounds.push("ui_close");
        pConsumedByBuild = true;
      } else if (!this.state.player.combatState.active) {
        ui.editMode = true;
        ui.combatStance = false;
        this.state.player.animState = "idle"; // clear combat_idle if entering from stance
        ui.inventoryOpen = true;
        ui.inventoryTab = "furniture";
        ui.aeternaOpen = false;
        // Close emote wheel if open
        if (this.state.player.emoteWheel.open) {
          this.state.player.emoteWheel.open = false;
          this.state.player.emoteWheel.selectedIndex = -1;
        }
        // Close device if open — can't build while on phone
        if (ui.deviceOpen) {
          this.deviceOverlay.close(this.state);
        }
        // Clear any pending furniture interaction
        this.interactionSystem?.clearPendingInteraction();
        ui.uiSounds.push("ui_open");
        pConsumedByBuild = true;
      } else {
        addToast(this.state, "Can't build during combat", "info", 2000);
      }
    }

    // C → toggle combat stance (room only, not in build mode or chat)
    if (keysJustPressed.has("c") && this.state.scene === "room" && !this.state.chat.inputOpen && ui.editMode) {
      addToast(this.state, "Can't draw weapon in build mode", "info", 2);
    }
    if (keysJustPressed.has("c") && this.state.scene === "room" && !this.state.chat.inputOpen && !ui.editMode) {
      if (ui.combatStance) {
        ui.combatStance = false;
        this.state.player.animState = "idle";
        ui.uiSounds.push("ui_close");
      } else {
        ui.combatStance = true;
        // Stand up from furniture if seated/sleeping before drawing weapon
        if (this.state.player.animState === "sit" || this.state.player.animState === "device_sit" || this.state.player.animState === "sleep") {
          this.state.player.position.elevation = 0;
        }
        // Close device if holding one — can't draw weapon while on phone
        if (ui.deviceOpen) {
          this.deviceOverlay.close(this.state);
        }
        this.state.player.animState = "combat_idle";
        this.state.player.targetPosition = undefined;
        this.state.player.movementQueue = [];
        // Clear any pending furniture interaction — mode change cancels it
        this.interactionSystem?.clearPendingInteraction();
        ui.uiSounds.push("ui_open");
      }
    }

    // T → toggle between starter room and test zone (room only, not in chat)
    if (keysJustPressed.has("t") && this.state.scene === "room" && !this.state.chat.inputOpen && !ui.editMode) {
      const isTestZone = this.state.currentRoom?.id === "room_test_zone";
      if (isTestZone) {
        this.state.currentRoom = createStarterRoom(this.state.player.id);
        this.state.player.position = { tileX: 3, tileY: 3, elevation: 0 };
        addToast(this.state, "Back to your room", "info", 2000);
      } else {
        this.state.currentRoom = createTestZone(this.state.player.id);
        this.state.player.position = { tileX: 10, tileY: 16, elevation: 0 };
        addToast(this.state, "Asset Test Zone", "info", 2000);
      }
      ui.uiSounds.push("ui_open");
    }

    // 1-5 → inventory tab quick-switch when inventory is open
    if (ui.inventoryOpen && !this.state.chat.inputOpen) {
      const tabKeys: Record<string, import("./state.js").InventoryTab> = {
        "1": "all", "2": "furniture", "3": "clothing", "4": "equipment", "5": "materials",
      };
      for (const [key, tab] of Object.entries(tabKeys)) {
        if (keysJustPressed.has(key)) { ui.inventoryTab = tab; ui.uiSounds.push("ui_tab"); break; }
      }
    }

    // ── Drain queued UI sounds from HUD/renderer ──
    if (this.audioEngine && ui.uiSounds.length > 0) {
      for (const s of ui.uiSounds) this.audioEngine.playInteraction(s);
      ui.uiSounds = [];
    }

    // Device toggle — D = hPhone, Shift+D = hPad, HUD button = deviceToggleRequest
    // Suppress D for device when other WASD keys are held (D is being used for movement)
    const deviceReq = ui.deviceToggleRequest;
    if (deviceReq) ui.deviceToggleRequest = null;
    const otherWasdHeld = this.input.state.keys.has("w") || this.input.state.keys.has("W")
      || this.input.state.keys.has("a") || this.input.state.keys.has("A")
      || this.input.state.keys.has("s") || this.input.state.keys.has("S");
    const dPressed = (keysJustPressed.has("d") || keysJustPressed.has("D")) && !otherWasdHeld;
    const shiftHeld = this.input.state.keys.has("Shift");
    const canDevice = this.state.scene === "room" && !this.state.chat.inputOpen && !this.state.player.combatState.active && !this.state.ui.editMode;

    if ((dPressed || deviceReq) && !canDevice && this.state.player.combatState.active) {
      addToast(this.state, "Can't use device during combat", "info", 2);
    }
    if ((dPressed || deviceReq) && canDevice) {
      // Determine which device type to toggle
      const requestedType: "phone" | "pad" = deviceReq === "pad" ? "pad"
        : (dPressed && shiftHeld) ? "pad"
        : "phone";

      const dev = this.state.player.device;
      const p = this.state.player;

      // Context-aware device anim: standing, walking, or sitting?
      const wasSitting = p.animState === "sit" || p.animState === "device_sit";
      const deviceAnim: import("./state.js").AnimState = wasSitting ? "device_sit" : "device";
      // What to restore when device closes
      const restoreAnim: import("./state.js").AnimState = wasSitting ? "sit" : "idle";

      // If same device type already open → close. If different type → swap.
      if (this.state.ui.deviceOpen && dev.activeDevice === requestedType) {
        // Toggle off
        this.deviceOverlay.close(this.state);
        p.animState = restoreAnim;
        if (this.audioEngine) this.audioEngine.playInteraction("device_close");
        haptic("light");
      } else if (this.state.ui.deviceOpen && dev.activeDevice !== requestedType) {
        // Swap device — close current, open requested
        this.deviceOverlay.close(this.state);
        this.deviceOverlay.open(this.state, requestedType);
        if (this.state.ui.deviceOpen) {
          p.animState = deviceAnim;
          const color = requestedType === "pad" ? dev.hPad.color : dev.hPhone.color;
          setDeviceContext(color, requestedType);
          if (this.audioEngine) this.audioEngine.playInteraction("device_open");
        }
      } else {
        // Open fresh
        this.deviceOverlay.toggle(this.state, requestedType);
        if (this.state.ui.deviceOpen) {
          p.animState = deviceAnim;
          const color = requestedType === "pad" ? dev.hPad.color : dev.hPhone.color;
          setDeviceContext(color, requestedType);
          if (this.audioEngine) this.audioEngine.playInteraction("device_open");
          haptic("medium");
          // If a specific app was requested, open it directly
          if (ui.deviceOpenToApp) {
            this.deviceOverlay.openApp(ui.deviceOpenToApp, this.state);
          }
        } else {
          p.animState = restoreAnim;
          if (this.audioEngine) this.audioEngine.playInteraction("device_close");
        }
      }
      ui.deviceOpenToApp = null;
    }

    // L → quick wardrobe (opens device to wardrobe app)
    if (keysJustPressed.has("l") && canDevice) {
      if (ui.deviceOpen && this.state.player.device.activeApp === "wardrobe") {
        ui.deviceToggleRequest = this.state.player.device.activeDevice ?? "phone";
      } else {
        ui.deviceToggleRequest = "phone";
        ui.deviceOpenToApp = "wardrobe";
      }
    }

    // R → rotate placement direction
    if (keysJustPressed.has("r") && ui.placingFurnitureType && !this.state.chat.inputOpen) {
      const ROT_CYCLE: Record<string, import("./state.js").Direction> = {
        E: "S", SE: "SW", S: "W", SW: "NW", W: "N", NW: "NE", N: "E", NE: "SE",
      };
      ui.placingDirection = ROT_CYCLE[ui.placingDirection] ?? "SE";
    }

    // ── Dialog input (keyboard + mouse) ────────────────────────────────────────
    if (this.state.dialog.open) {
      const dlg = this.state.dialog;

      // ── Mouse hover: update hoveredOption based on pointer position ──
      dlg.hoveredOption = -1;
      for (let i = 0; i < dialogHitZones.length; i++) {
        const hz = dialogHitZones[i];
        if (inputState.pointer.x >= hz.x && inputState.pointer.x <= hz.x + hz.w &&
            inputState.pointer.y >= hz.y && inputState.pointer.y <= hz.y + hz.h) {
          dlg.hoveredOption = i;
          break;
        }
      }

      // ── Mouse click: select option or focus input ──
      if (inputState.clickPosition) {
        const mx = inputState.clickPosition.x;
        const my = inputState.clickPosition.y;

        // Check option hit zones
        for (const hz of dialogHitZones) {
          if (mx >= hz.x && mx <= hz.x + hz.w && my >= hz.y && my <= hz.y + hz.h) {
            const opt = dlg.options.find(o => o.id === hz.optionId);
            if (opt && !opt.disabled) {
              handleDialogOption(this.state, opt.id);
              if (this.audioEngine) this.audioEngine.playInteraction("ui_click");
            }
            break;
          }
        }

        // All clicks consumed while dialog is open — nothing behind it responds
        this.state.ui.uiClickConsumed = true;
      }

      // ── Number keys 1-4 select dialog options ──
      for (let i = 1; i <= 4; i++) {
        if (keysJustPressed.has(String(i)) && !dlg.showFreeInput) {
          const opt = dlg.options[i - 1];
          if (opt && !opt.disabled) {
            handleDialogOption(this.state, opt.id);
            if (this.audioEngine) this.audioEngine.playInteraction("ui_click");
          }
        }
      }

      // ── Free text keyboard capture ──
      if (dlg.showFreeInput) {
        for (const key of keysJustPressed) {
          if (key === "Backspace") {
            dlg.freeInputValue = dlg.freeInputValue.slice(0, -1);
          } else if (key === "Enter" && dlg.freeInputValue.trim()) {
            handleFreeTextInput(this.state, dlg.freeInputValue.trim());
            if (this.audioEngine) this.audioEngine.playInteraction("device_tap");
          } else if (key.length === 1 && dlg.freeInputValue.length < 200) {
            // Single printable character
            dlg.freeInputValue += key;
          }
        }
      } else {
        // Enter sends free text even when input isn't explicitly shown (legacy)
        if (keysJustPressed.has("Enter") && dlg.freeInputValue.trim()) {
          handleFreeTextInput(this.state, dlg.freeInputValue.trim());
          if (this.audioEngine) this.audioEngine.playInteraction("device_tap");
        }
      }

      // Escape closes dialog
      if (keysJustPressed.has("Escape")) {
        closeDialog(this.state);
        if (this.audioEngine) this.audioEngine.playInteraction("ui_click");
      }
      // Block all other input while dialog is open
      return;
    }

    // ── Quest log toggle ─────────────────────────────────────────────────────
    if (keysJustPressed.has("l") || keysJustPressed.has("L")) {
      if (!this.state.chat.inputOpen) {
        this.state.questUI.logOpen = !this.state.questUI.logOpen;
        if (this.audioEngine) this.audioEngine.playInteraction("ui_click");
      }
    }

    // Quest log Escape
    if (this.state.questUI.logOpen && keysJustPressed.has("Escape")) {
      this.state.questUI.logOpen = false;
      return;
    }

    // Escape → dismiss in priority order: device → chat → selection → context → move → place → panels
    if (keysJustPressed.has("Escape")) {
      if (this.state.player.device.editCallback) {
        // Cancel active text edit in device app
        this.state.player.device.editCallback = null;
        this.state.player.device.editPlaceholder = "";
        this.state.chat.inputOpen = false;
        this.input.toggleChat(false);
      } else if (ui.deviceOpen) {
        this.deviceOverlay.close(this.state);
        // Restore to sitting if character was sitting before device, otherwise idle
        const prevAnim = this.state.player.animState;
        if (prevAnim === "device_sit") {
          this.state.player.animState = "sit";
        } else if (prevAnim === "device_walk") {
          this.state.player.animState = "walk";
        } else {
          this.state.player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";
        }
        if (this.audioEngine) this.audioEngine.playInteraction("device_close");
      } else if (this.state.chat.inputOpen) {
        this.state.chat.inputOpen = false;
        this.state.chat.aeternaMode = false;
        this.input.toggleChat(false);
      } else if (ui.selectedFurniture) {
        ui.selectedFurniture = null;
      } else if (ui.furnitureContextId) {
        ui.furnitureContextId = null;
      } else if (ui.movingFurnitureId) {
        this.cancelMove();
      } else if (ui.placingFurnitureType) {
        ui.placingFurnitureType = null;
      } else if (ui.inventoryOpen) {
        ui.inventoryOpen = false;
      } else if (ui.editMode) {
        // Exit build mode entirely — clean up all sub-states
        ui.editMode = false;
        ui.selectedFurniture = null;
        ui.furnitureContextId = null;
        ui.placingFurnitureType = null;
        ui.movingFurnitureId = null;
        ui.movingOriginalTile = null;
        ui.inventoryOpen = false;
        ui.pendingBuildAction = null;
      } else if (this.state.player.emoteWheel.open) {
        this.state.player.emoteWheel.open = false;
        this.state.player.emoteWheel.selectedIndex = -1;
      } else if (ui.combatStance) {
        // Sheathe weapon
        ui.combatStance = false;
        this.state.player.animState = "idle";
      } else if (ui.aeternaOpen) {
        ui.aeternaOpen = false;
      }
    }

    // Context menu keyboard shortcuts (R=Rotate, M=Move, G=Pick Up)
    if (ui.furnitureContextId && !this.state.chat.inputOpen) {
      const ctxFid = ui.furnitureContextId;
      const ctxRoom = this.state.currentRoom;
      const ctxPlacement = ctxRoom?.furniture.find(f => f.id === ctxFid);
      if (ctxPlacement && ctxRoom) {
        if (keysJustPressed.has("r") || keysJustPressed.has("R")) {
          const oldFP = getFootprintTiles(ctxPlacement);
          for (const t of oldFP) {
            const tile = ctxRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = undefined; tile.walkable = true; }
          }
          const cycle: import("./state.js").Direction[] = ["SE","SW","NW","NE"];
          const idx = cycle.indexOf(ctxPlacement.direction as "SE"|"SW"|"NW"|"NE");
          ctxPlacement.direction = cycle[(idx + 1) % cycle.length];
          const newFP = getFootprintTiles(ctxPlacement);
          for (const t of newFP) {
            const tile = ctxRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = ctxPlacement.id; tile.walkable = false; }
          }
          ui.furnitureContextId = null;
        } else if (keysJustPressed.has("m") || keysJustPressed.has("M")) {
          ui.movingFurnitureId = ctxFid;
          ui.movingOriginalTile = { tileX: ctxPlacement.tileX, tileY: ctxPlacement.tileY };
          ui.placingFurnitureType = ctxPlacement.type;
          ui.placingDirection = ctxPlacement.direction as import("./state.js").Direction;
          const fp = getFootprintTiles(ctxPlacement);
          for (const t of fp) {
            const tile = ctxRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = undefined; tile.walkable = true; }
          }
          ui.furnitureContextId = null;
        } else if (keysJustPressed.has("g") || keysJustPressed.has("G")) {
          const fpIdx = ctxRoom.furniture.findIndex(f => f.id === ctxFid);
          if (fpIdx !== -1) {
            const p = ctxRoom.furniture[fpIdx];
            const fp = getFootprintTiles(p);
            for (const t of fp) {
              const tile = ctxRoom.tiles[t.y]?.[t.x];
              if (tile) { tile.furniture = undefined; tile.walkable = true; }
            }
            // Material-appropriate pickup sound
            const pickupMat = getFurnitureType(p.type)?.material ?? "wood";
            const pickupSound = pickupMat === "stone" ? "place_stone"
              : pickupMat === "metal" ? "place_metal" : "place_wood";
            ui.uiSounds.push(pickupSound);
            ctxRoom.furniture.splice(fpIdx, 1);
            const existing = this.state.player.inventory.find(
              it => it.type === "furniture" && it.furnitureTypeId === p.type
            );
            if (existing) {
              existing.quantity += 1;
            } else {
              this.state.player.inventory.push({
                id: `inv_pickup_${Date.now()}`,
                type: "furniture",
                name: p.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                quantity: 1,
                rarity: "common",
                acquiredAt: Date.now(),
                furnitureTypeId: p.type,
              });
            }
          }
          ui.furnitureContextId = null;
        }
      }
    }

    // Edit mode keyboard shortcuts + HUD button actions (R=Rotate, M=Move, G=Pick Up on selected furniture)
    const pendingAction = ui.pendingBuildAction;
    if (pendingAction) ui.pendingBuildAction = null; // consume immediately
    if (ui.editMode && ui.selectedFurniture && !ui.furnitureContextId && !this.state.chat.inputOpen) {
      const selFid = ui.selectedFurniture;
      const selRoom = this.state.currentRoom;
      const selPlacement = selRoom?.furniture.find(f => f.id === selFid);
      if (selPlacement && selRoom) {
        if (keysJustPressed.has("r") || keysJustPressed.has("R") || pendingAction === "rotate") {
          const oldFP = getFootprintTiles(selPlacement);
          for (const t of oldFP) {
            const tile = selRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = undefined; tile.walkable = true; }
          }
          const cycle: import("./state.js").Direction[] = ["SE","SW","NW","NE"];
          const idx = cycle.indexOf(selPlacement.direction as "SE"|"SW"|"NW"|"NE");
          selPlacement.direction = cycle[(idx + 1) % cycle.length];
          const newFP = getFootprintTiles(selPlacement);
          for (const t of newFP) {
            const tile = selRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = selPlacement.id; tile.walkable = false; }
          }
          ui.uiSounds.push("ui_tab");
        } else if (keysJustPressed.has("m") || keysJustPressed.has("M") || pendingAction === "move") {
          ui.movingFurnitureId = selFid;
          ui.movingOriginalTile = { tileX: selPlacement.tileX, tileY: selPlacement.tileY };
          ui.placingFurnitureType = selPlacement.type;
          ui.placingDirection = selPlacement.direction as import("./state.js").Direction;
          const fp = getFootprintTiles(selPlacement);
          for (const t of fp) {
            const tile = selRoom.tiles[t.y]?.[t.x];
            if (tile) { tile.furniture = undefined; tile.walkable = true; }
          }
          ui.selectedFurniture = null;
        } else if (keysJustPressed.has("g") || keysJustPressed.has("G") || pendingAction === "pickup") {
          const fpIdx = selRoom.furniture.findIndex(f => f.id === selFid);
          if (fpIdx !== -1) {
            const p = selRoom.furniture[fpIdx];
            const fp = getFootprintTiles(p);
            for (const t of fp) {
              const tile = selRoom.tiles[t.y]?.[t.x];
              if (tile) { tile.furniture = undefined; tile.walkable = true; }
            }
            // Material-appropriate pickup sound
            const editPickupMat = getFurnitureType(p.type)?.material ?? "wood";
            const editPickupSound = editPickupMat === "stone" ? "place_stone"
              : editPickupMat === "metal" ? "place_metal" : "place_wood";
            ui.uiSounds.push(editPickupSound);
            selRoom.furniture.splice(fpIdx, 1);
            const existing = this.state.player.inventory.find(
              it => it.type === "furniture" && it.furnitureTypeId === p.type
            );
            if (existing) {
              existing.quantity += 1;
            } else {
              this.state.player.inventory.push({
                id: `inv_pickup_${Date.now()}`,
                type: "furniture",
                name: p.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
                quantity: 1,
                rarity: "common",
                acquiredAt: Date.now(),
                furnitureTypeId: p.type,
              });
            }
          }
          ui.selectedFurniture = null;
        }
      }
    }

    // ── Ability keys (1-6) — cast from skill bar ─────────────────────────
    // Guard: don't fire abilities when sitting/sleeping, in build mode, or inventory open
    const _anim = this.state.player.animState;
    const canCast = _anim !== "sit" && _anim !== "sleep" && _anim !== "device_sit" && !ui.editMode;
    if (this.state.scene === "room" && !this.state.chat.inputOpen && !this.state.player.death.isDead && !ui.inventoryOpen && canCast) {
      const abilityKeys = ["1", "2", "3", "4", "5", "6"];
      const now = performance.now() / 1000;
      for (let i = 0; i < abilityKeys.length; i++) {
        if (keysJustPressed.has(abilityKeys[i])) {
          const slot = this.state.player.abilityBar.slots[i];
          if (slot) {
            const def = getAbility(slot.id);
            if (def) {
              const ember = this.state.player.combat.ember;
              const onCD = now < slot.cooldownEnd;
              const onGCD = now < this.state.player.abilityBar.globalCooldownEnd;
              if (!onCD && !onGCD && ember.current >= def.cost) {
                // Consume EP
                ember.current -= def.cost;
                // Set cooldown
                slot.cooldownEnd = now + def.cooldown;
                // Set GCD if applicable
                if (def.triggersGCD) {
                  this.state.player.abilityBar.globalCooldownEnd = now + 1.0; // 1s GCD
                }
                // Consume charges
                if (def.maxCharges > 1 && slot.charges > 0) {
                  slot.charges--;
                }
                // Execute ability effects (damage, heals, buffs)
                executeAbility(this.state, slot.id);
                // Play ability sound
                ui.uiSounds.push(def.visual.sound);
              } else {
                // Can't cast — play error sound
                ui.uiSounds.push("ui_error");
              }
            }
          }
          break;
        }
      }
    }

    // ── Emote wheel (E key) ─────────────────────────────────────────────
    if (keysJustPressed.has("e") && this.state.scene === "room" && !this.state.chat.inputOpen && !this.state.player.combatState.active && !ui.editMode && !this.state.player.death.isDead) {
      if (this.state.player.emoteWheel.open) {
        // Close wheel — fire selected emote if any
        const sel = this.state.player.emoteWheel.selectedIndex;
        if (sel >= 0 && sel < EMOTES.length) {
          triggerEmote(this.state, EMOTES[sel].id);
        } else {
          closeEmoteWheel(this.state);
        }
      } else {
        openEmoteWheel(this.state);
        ui.uiSounds.push("ui_open");
      }
    }

    // ── PvP toggle (P key) ─────────────────────────────────────────────
    if (keysJustPressed.has("p") && this.state.scene === "room" && !this.state.chat.inputOpen && !ui.editMode && !pConsumedByBuild) {
      const msg = togglePvP(this.state);
      addToast(this.state, msg, "system");
      ui.uiSounds.push("ui_click");
    }

    // ── Duel accept/decline (Y/N keys) ─────────────────────────────────
    if (this.state.player.duel.pendingChallenge && this.state.scene === "room" && !this.state.chat.inputOpen) {
      if (keysJustPressed.has("y")) {
        const msg = acceptDuel(this.state);
        addToast(this.state, msg, "pvp");
        addCombatLogEntry(msg, "#e88020");
        ui.uiSounds.push("ui_click");
      } else if (keysJustPressed.has("n")) {
        declineDuel(this.state);
        addToast(this.state, "Duel declined", "system");
        ui.uiSounds.push("ui_close");
      }
    }

    // ── Respawn (R key) when dead ────────────────────────────────────────
    if (keysJustPressed.has("r") && this.state.player.death.isDead && this.state.player.death.canRespawn) {
      const respawned = respawnPlayer(this.state);
      if (respawned) {
        addToast(this.state, "You have been revived", "system");
        ui.uiSounds.push("ui_open");
      }
    }

    // ── Tab to cycle target (creature targeting) ─────────────────────────
    if (keysJustPressed.has("Tab") && this.state.scene === "room" && !this.state.chat.inputOpen) {
      // Cycle through nearby hostile creatures
      const creatures = this.state.creatures;
      if (creatures.length > 0) {
        const currentTargetId = this.state.player.target.id;
        let nextIndex = 0;
        if (currentTargetId) {
          const currentIndex = creatures.findIndex(c => c.id === currentTargetId);
          nextIndex = (currentIndex + 1) % creatures.length;
        }
        targetCreature(this.state, creatures[nextIndex].id);
        ui.uiSounds.push("ui_click");
      }
    }

    // ── F to set focus target (copy current target to focus) ──────────────
    if (keysJustPressed.has("f") && this.state.scene === "room" && !this.state.chat.inputOpen && !ui.editMode) {
      const t = this.state.player.target;
      if (t.id) {
        const ft = this.state.player.focusTarget;
        ft.id = t.id;
        ft.type = t.type;
        ft.name = t.name;
        ft.level = t.level;
        ft.hp = t.hp;
        ft.maxHp = t.maxHp;
        ft.disposition = t.disposition;
        ft.setAt = performance.now() / 1000;
        addToast(this.state, `Focus: ${t.name}`, "system");
        ui.uiSounds.push("ui_click");
      } else {
        // Clear focus when pressing F with no target
        this.state.player.focusTarget.id = null;
        this.state.player.focusTarget.type = null;
        this.state.player.focusTarget.name = null;
      }
    }

    // ── Escape to clear target ────────────────────────────────────────────
    if (keysJustPressed.has("Escape") && this.state.player.target.id && !this.state.chat.inputOpen) {
      this.state.player.target.id = null;
      this.state.player.target.type = null;
      this.state.player.target.name = null;
      this.state.player.target.marker = null;
    }

    // ── D to toggle death recap when dead ─────────────────────────────────
    if (keysJustPressed.has("d") && this.state.player.death.isDead && !this.state.chat.inputOpen) {
      this.state.player.deathRecap.visible = !this.state.player.deathRecap.visible;
    }

    // ── H to toggle PvP stats panel ──────────────────────────────────────
    if (keysJustPressed.has("h") && this.state.scene === "room" && !this.state.chat.inputOpen && !ui.editMode) {
      ui.pvpStatsOpen = !ui.pvpStatsOpen;
      ui.uiSounds.push("ui_click");
    }

    // ── M to toggle full-screen room map overlay ─────────────────────────
    if (keysJustPressed.has("m") && this.state.scene === "room" && !this.state.chat.inputOpen) {
      ui.mapOpen = !ui.mapOpen;
      if (ui.mapOpen) { ui.mapPanX = 0; ui.mapPanY = 0; }
      ui.uiSounds.push(ui.mapOpen ? "ui_open" : "ui_close");
    }
    // Escape closes the map
    if (keysJustPressed.has("Escape") && ui.mapOpen) {
      ui.mapOpen = false;
      ui.uiSounds.push("ui_close");
    }

    // Chat submit
    if (keysJustPressed.has("ChatSubmit") && this.state.chat.inputOpen) {
      // Device app text edit intercept — apps set editCallback when they need input
      const editCb = this.state.player.device.editCallback;
      if (editCb) {
        const text = this.input.consumeChatMessage();
        this.state.player.device.editCallback = null;
        this.state.player.device.editPlaceholder = "";
        this.state.chat.inputOpen = false;
        if (text) editCb(text);
      } else {
        const wasAeternaMode = this.state.chat.aeternaMode;
        const text = this.input.consumeChatMessage();
        if (text) {
          if (wasAeternaMode && this.state.aeternaConductor) {
            // Route to Aeterna conversation
            this.state.aeternaConductor.processPlayerMessage(text, {
              composedMs: -1,
              charCount: text.length,
            });
            announceToScreenReader(`You: ${text}`);
            haptic("light");
          } else {
            // Room chat — speech bubble
            const msg = {
              id: `msg_${Date.now()}`,
              senderId: this.state.player.id,
              senderName: this.state.player.name,
              text,
              timestamp: Date.now(),
              bubbleUntil: Date.now() + 5000,
            };
            this.state.chat.messages.push(msg);
            this.state.chat.activeBubbles.push({
              senderId: this.state.player.id,
              text,
              until: Date.now() + 5000,
            });
            announceToScreenReader(`${this.state.player.name}: ${text}`);
            haptic("light");
          }
        }

        if (wasAeternaMode && ui.deviceOpen && this.state.player.device.activeApp === "hearth") {
          // Stay in aeterna mode — reopen input for continuous conversation
          this.state.chat.aeternaMode = true;
          this.state.chat.inputOpen = true;
          // Re-position over compose bar (sync will pick up next frame)
        } else {
          this.state.chat.inputOpen = false;
          this.state.chat.aeternaMode = false;
        }
      }
    }
  }

  // ─── Transition Logic ─────────────────────────────────────────────────────

  private updateTransition(dt: number, _inputState: InputState): void {
    if (this.state.scene !== "transition") return;
    if (!this.state.transitionTarget) return;

    // Advance progress — slower from title so the audio has time to breathe
    const speed = this.state.previousScene === "title" ? 0.8 : 2.5;
    this.state.transitionProgress += dt * speed; // title: ~1.25s, others: 0.4s

    if (this.state.transitionProgress >= 1.0) {
      // Commit the scene switch at the halfway point
      const target = this.state.transitionTarget;
      this.state.scene = target;
      this.state.transitionTarget = null;
      this.state.transitionProgress = 0;

      // Set up the new scene
      this.onSceneEnter(target);
    }
  }

  /** The player's personal room — saved when traveling away so it can be restored */
  private savedPlayerRoom: import("./state.js").Room | null = null;

  /** Enter a zone: generate room, spawn creatures, switch to room scene */
  private enterZone(zoneId: string): void {
    const zone = getZone(this.state.world, zoneId);
    if (!zone) return;

    // Fire discovery toast on first visit (explored is still 0 or barely set)
    if (zone.explored <= 0.1 && zoneId !== "ember_lane") {
      addToast(this.state, "Discovered: " + zone.name, "discovery");
    }

    // If returning to home zone, restore the player's personal room
    const isReturningHome = zoneId === "ember_lane" && this.savedPlayerRoom;
    if (isReturningHome) {
      this.state.currentRoom = this.savedPlayerRoom;
      this.savedPlayerRoom = null;
    } else {
      this.state.currentRoom = generateZoneRoom(zone);
    }

    // Clear stale player state from previous zone
    this.state.player.movementQueue = [];
    this.state.player.moveProgress = 0;
    this.state.player.targetPosition = undefined;
    this.state.player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";

    // Close all UI panels — they shouldn't persist across zones
    this.state.ui.inventoryOpen = false;
    this.state.ui.aeternaOpen = false;
    this.state.ui.deviceOpen = false;
    // Reset build mode — furniture references are stale in the new room
    this.state.ui.editMode = false;
    this.state.ui.selectedFurniture = null;
    this.state.ui.furnitureContextId = null;
    this.state.ui.placingFurnitureType = null;
    this.state.ui.movingFurnitureId = null;
    this.state.ui.movingOriginalTile = null;
    this.state.ui.pendingBuildAction = null;

    // Clear combat state from previous zone
    this.state.player.combatState.active = false;
    this.state.player.combatState.targetId = null;
    this.state.player.combatState.combo = 0;
    this.state.player.combatState.strikeQueued = false;
    this.state.player.combatState.strikeQueuedAt = 0;
    this.state.player.combatState.staggerTimer = 0;
    this.state.player.combatState.damageNumbers = [];
    clearCreatureCombatHp();

    // Clean up per-creature memory (attention states, behavior accumulators)
    // before replacing the creature array. Without this, module-level Maps
    // in renderer.ts and update.ts leak entries for every creature ever seen.
    for (const c of this.state.creatures) {
      cleanupCreature(c.id);
      cleanupCreatureAttention(c.id);
    }
    for (const dc of this.state.dyingCreatures) {
      cleanupCreature(dc.creature.id);
      cleanupCreatureAttention(dc.creature.id);
    }
    this.state.dyingCreatures = [];
    this.state.creatureHitTimes.clear();

    if (isReturningHome) {
      // Returning to personal room — restore player creatures, no zone NPCs/mobs
      this.state.creatures = [...this.state.playerCreatures];
      this.state.npcs = [];
    } else {
      // Spawn zone-appropriate creatures
      const spawns = getZoneCreatures(zone);
      this.state.creatures = [];
      const room = this.state.currentRoom!;
      for (const spawn of spawns) {
        // Validate spawn position — ensure tile is walkable
        let sx = spawn.tileX;
        let sy = spawn.tileY;
        const spawnTile = room.tiles[sy]?.[sx];
        if (!spawnTile || !spawnTile.walkable) {
          // Spiral search outward to find nearest walkable tile
          let found = false;
          for (let r = 1; r < Math.max(room.width, room.height) && !found; r++) {
            for (let dy = -r; dy <= r && !found; dy++) {
              for (let dx = -r; dx <= r && !found; dx++) {
                if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
                const tx = spawn.tileX + dx;
                const ty = spawn.tileY + dy;
                if (tx >= 0 && tx < room.width && ty >= 0 && ty < room.height) {
                  const t = room.tiles[ty][tx];
                  if (t && t.walkable) {
                    sx = tx;
                    sy = ty;
                    found = true;
                  }
                }
              }
            }
          }
        }
        const elevation = room.tiles[sy]?.[sx]?.elevation ?? 0;
        const creature = createCreature(spawn.speciesId, {
          tileX: sx,
          tileY: sy,
          elevation,
        }, {
          name: spawn.name,
          palette: spawn.palette,
        });
        if (creature) {
          this.state.creatures.push(creature);
        }
      }

      // Spawn zone NPCs
      this.state.npcs = getZoneNpcs(zoneId);
    }

    // Notify audio engine of zone type and weather for ambient selection
    if (this.audioEngine) {
      this.audioEngine.setZoneType(zone.type);
      this.audioEngine.setWeather(zone.defaultWeather ?? "clear");

      // Room-aware reverb: compute dominant material from floor tiles + room size
      const room = this.state.currentRoom;
      if (room) {
        const roomSize = room.width * room.height;
        const matCounts: Record<string, number> = {};
        for (const row of room.tiles) {
          for (const tile of row) {
            if (tile) {
              const m = tile.type ?? "wood";
              matCounts[m] = (matCounts[m] ?? 0) + 1;
            }
          }
        }
        let dominant = "wood"; let best = 0;
        for (const [m, c] of Object.entries(matCounts)) {
          if (c > best) { best = c; dominant = m; }
        }
        this.audioEngine.setRoomReverb(roomSize, dominant);
      }
    }

    this.state.scene = "room";
    this.onSceneEnter("room");
  }

  private onSceneEnter(scene: import("./state.js").Scene): void {
    if (scene === "room") {
      // Create starter room if none exists
      if (!this.state.currentRoom) {
        this.state.currentRoom = createStarterRoom(this.state.player.id);
      }

      const room = this.state.currentRoom;

      // Place player on a walkable tile near room center
      {
        const cx = Math.floor(room.width / 2);
        const cy = Math.floor(room.height / 2);
        let spawnX = cx, spawnY = cy;
        // If center isn't walkable, spiral outward to find one
        const centerTile = room.tiles[cy]?.[cx];
        if (!centerTile || !centerTile.walkable) {
          let found = false;
          for (let r = 1; r < Math.max(room.width, room.height) && !found; r++) {
            for (let dy = -r; dy <= r && !found; dy++) {
              for (let dx = -r; dx <= r && !found; dx++) {
                if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue; // only ring
                const tx = cx + dx, ty = cy + dy;
                if (tx >= 0 && tx < room.width && ty >= 0 && ty < room.height) {
                  const t = room.tiles[ty][tx];
                  if (t && t.walkable) {
                    spawnX = tx;
                    spawnY = ty;
                    found = true;
                  }
                }
              }
            }
          }
        }
        this.state.player.position = { tileX: spawnX, tileY: spawnY, elevation: room.tiles[spawnY]?.[spawnX]?.elevation ?? 0 };
      }

      // ── Compute ideal zoom to fit the room on screen ──────────────────
      // Isometric extent: the room's bounding box in screen space (pre-zoom).
      // For an NxM isometric grid, the width spans (N+M) * TILE_W_HALF
      // and the height spans (N+M) * TILE_H_HALF + wall height + UI chrome.
      const isoW = (room.width + room.height) * 32;   // TILE_W_HALF = 32
      const isoH = (room.width + room.height) * 16 + 80; // +80 for walls + headroom
      const cw = this.state.canvasWidth;
      const ch = this.state.canvasHeight;

      // The room should comfortably fit within the viewport with generous
      // breathing room. Use the SMALLER dimension as the constraint.
      const fitW = cw / isoW;
      const fitH = ch / isoH;
      const fitZoom = Math.min(fitW, fitH);

      // Scale down to ~70% of "perfectly filling" so the room sits with margins
      const idealZoom = fitZoom * 0.7;

      // Clamp: at least ZOOM_MIN so it's never tiny, at most 1.5 so pixel art
      // stays readable without being overwhelming
      const zoom = Math.max(ZOOM_MIN, Math.min(1.5, idealZoom));
      this.state.camera.zoom = zoom;
      this.state.camera.targetZoom = zoom;

      // ── Snap camera to room center on entry ────────────────────────────
      // Use +0.5 for tile center, consistent with camera follow math
      const midX = room.width / 2 + 0.5;
      const midY = room.height / 2 + 0.5;
      const sx = (midX - midY) * 32;
      const sy = (midX + midY) * 16;
      const snapX = cw / 2 - sx * zoom;
      const snapY = ch / 2 - sy * zoom;
      this.state.camera.x = snapX;
      this.state.camera.y = snapY;
      this.state.camera.targetX = snapX;
      this.state.camera.targetY = snapY;

      // Lazily create room systems on first entry
      if (!this.particleSystem) {
        this.particleSystem = new ParticleSystem();
      }
      if (!this.interactionSystem) {
        this.interactionSystem = new InteractionSystem();
      }

      // Queue introductory whispers on first room entry
      if (!this.roomRenderer) {
        this.roomRenderer = new RoomRenderer(this.canvas, this.ctx);
      }
      if (this.state.previousScene === "character_create" || this.state.previousScene === "title") {
        const name = this.state.player.name;
        this.roomRenderer.queueWhispers([
          `Welcome home, ${name}.`,
          "A room learns the shape of the person who lives in it. This one is still learning.",
          "Click anywhere to walk. The room is yours to explore.",
        ]);
      }
    }
  }

  // ─── Character Creation Update ──────────────────────────────────────────

  private updateCharacterCreate(dt: number, inputState: InputState): void {
    if (!this.charCreateRenderer) {
      this.charCreateRenderer = new CharacterCreateRenderer(this.canvas, this.ctx);
    }
    this.charCreateRenderer.update(inputState, this.state, dt);

    // When the player finishes creating, transition to room
    if (this.charCreateRenderer.ready) {
      this.state.player.name = this.charCreateRenderer.getPlayerName();

      // Apply device colors from creation
      const deviceColors = this.charCreateRenderer.getDeviceColors();
      this.state.player.device.hPhone.color = deviceColors.phoneColor;
      this.state.player.device.hPad.color = deviceColors.padColor;

      // Grant starter items from accessories/hat/held choices
      const starterItems = this.charCreateRenderer.getStarterItems();
      for (const item of starterItems) {
        this.state.player.inventory.push(item);
      }

      if (!this.state.currentRoom) {
        this.state.currentRoom = createStarterRoom(this.state.player.id);
      }

      // Equip starter abilities so the player has something on 1-2-3
      assignStarterAbilities(this.state);
      // If any starter weapon was granted, populate weapon ability slots 4-5-6
      updateWeaponAbilities(this.state);

      beginTransition(this.state, "room");
    }
  }

  // ─── Room Update ─────────────────────────────────────────────────────────

  private updateRoom(dt: number): void {
    // Placeholder for room systems: furniture tick, plant growth, pet AI, etc.
    // These modules will be built incrementally as the codebase grows.
    // For now we just tick ambient bubble expiry.

    const now = Date.now();
    this.state.chat.activeBubbles = this.state.chat.activeBubbles.filter(
      (b) => b.until > now
    );

    // Expire notifications
    this.state.ui.notifications = this.state.ui.notifications.filter(
      (n) => n.expiresAt > now
    );

    // Update interaction system
    if (this.interactionSystem) {
      const result = this.interactionSystem.update(this.state, this.input.state);
      if (result?.sound && this.audioEngine) {
        this.audioEngine.playInteraction(result.sound);
      }

      // Consume interact-prompt hit region click (tooltip "Click to sit" etc.)
      const pendingId = this.state.ui.pendingInteractFurnitureId;
      if (pendingId) {
        this.state.ui.pendingInteractFurnitureId = null;
        const promptResult = this.interactionSystem.interactWithFurniture(this.state, pendingId);
        if (promptResult?.sound && this.audioEngine) {
          this.audioEngine.playInteraction(promptResult.sound);
        }
      }
    }

    // Update audio ambient
    if (this.audioEngine && this.state.currentRoom) {
      const mood = this.state.currentRoom.mood;
      this.audioEngine.update(mood, this.state.time.gameHour, dt);
    }

    // Update particles
    if (this.particleSystem && this.state.currentRoom) {
      const cam: Camera = { x: this.state.camera.x, y: this.state.camera.y, zoom: this.state.camera.zoom };
      this.particleSystem.update(dt, this.state, cam, this.perf.qualityLevel);
    }

    // Update footstep dust particles — physics + expiry
    {
      const fps = this.state.footstepParticles;
      for (let i = fps.length - 1; i >= 0; i--) {
        const p = fps[i];
        p.life -= dt;
        if (p.life <= 0) {
          fps.splice(i, 1);
          continue;
        }
        // Simple physics: drift outward/upward, decelerate
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= Math.exp(-5 * dt);  // strong air drag
        p.vy *= Math.exp(-3 * dt);
      }
    }

    // Update Aeterna presence state
    if (this.state.aeterna && this.state.currentRoom) {
      const room    = this.state.currentRoom;
      const mood    = room.mood ?? { warmth: 0.5, coziness: 0.5, mystery: 0.2 };
      const ftypes  = new Set(room.furniture.map((f) => f.type));
      updatePresence(
        this.state.aeterna,
        dt,
        this.state.time.gameHour,
        this.state.ui.aeternaOpen,
        mood,
        ftypes,
        false,
        this.state.time.season,
      );
    }

    // ── Aeterna Conductor: tick neural pipeline + typewriter ──────────
    if (this.state.currentRoom) {
      // Lazy initialization: create conductor on first room entry
      if (!this.state.aeternaConductor) {
        const conductor = new AeternaConductor(42);
        conductor.beginSession();
        conductor.wireProactiveInitiation();
        // Configure EntityZero API — the endpoint is checked lazily on first call.
        // When the server isn't running, the conductor gracefully falls back to offline.
        conductor.configureAPI({
          endpoint: "http://localhost:8080/v1/chat/completions",
          model: "e0-0.8b",
          timeout: 15_000,
        });
        conductor.updateRoomContext(this.state.currentRoom, this.state.time);
        this.state.aeternaConductor = conductor;
      }

      const conductor = this.state.aeternaConductor;
      conductor.tick(dt, this.state.time.gameHour, this.state.aeterna);
      conductor.updateTime(this.state.time);

      // Feed room context when room changes (furniture moved, lighting toggled)
      // The conductor caches internally, so this is cheap when nothing changed
      if (this.state.currentRoom) {
        conductor.updateRoomContext(this.state.currentRoom, this.state.time);
      }
    }
  }

  // ─── Creature Update ──────────────────────────────────────────────────────

  private updateCreatures(dt: number): void {
    const room = this.state.currentRoom;
    if (!room) return;

    const time = this.state.time.real / 1000;
    const gameHour = this.state.time.gameHour;
    const mood = room.mood;
    const player = this.state.player;

    const isWalkable = (tx: number, ty: number): boolean => {
      if (tx < 0 || tx >= room.width || ty < 0 || ty >= room.height) return false;
      const tile = room.tiles[ty]?.[tx];
      return tile ? tile.walkable : false;
    };

    const ctx: CreatureUpdateContext = {
      time,
      dt,
      gameHour,
      mood: {
        warmth: mood.warmth,
        energy: mood.energy,
        mystery: mood.mystery,
        noise: mood.noise,
        nature: mood.nature,
      },
      playerPos: { tileX: player.position.tileX, tileY: player.position.tileY },
      allCreatures: this.state.creatures,
      isWalkable,
      roomWidth: room.width,
      roomHeight: room.height,
    };

    for (const creature of this.state.creatures) {
      updateCreature(creature, ctx);
    }
  }

  // ─── Character Update ─────────────────────────────────────────────────────

  private updateCharacters(dt: number, inputState: InputState): void {
    // Furniture placement click — intercept before normal movement
    if (
      this.state.ui.placingFurnitureType &&
      !this.state.ui.uiClickConsumed &&
      inputState.clickedTile &&
      this.state.currentRoom
    ) {
      const { tx, ty } = inputState.clickedTile;
      this.handlePlacement(tx, ty);
      return;
    }

    // ── WASD / Arrow key continuous movement ──────────────────────────────
    // Screen-space WASD maps to isometric tile directions:
    //   W (screen up)    → tile (-1, -1) → N      D (screen right)  → tile (+1, -1) → E
    //   S (screen down)  → tile (+1, +1) → S      A (screen left)   → tile (-1, +1) → W
    //   W+D → NE, S+A → SW, W+A → NW, S+D → SE
    //
    // Conversion: tileDX = screenDX + screenDY,  tileDY = -screenDX + screenDY
    {
      const { dx: sDX, dy: sDY } = inputState.moveDir; // screen-space direction
      const wasdBlocked = this.state.chat.inputOpen || this.state.ui.deviceOpen
        || this.state.ui.inventoryOpen || this.state.dialog?.open
        || this.state.player.death.isDead
        || this.state.player.animState === "sit" || this.state.player.animState === "sleep"
        || this.state.player.animState === "device_sit"
        || this.state.player.animState === "interact";
      const wasdActive = !wasdBlocked && (sDX !== 0 || sDY !== 0) && this.state.currentRoom;

      if (wasdActive) {
        const room = this.state.currentRoom!;
        const player = this.state.player;

        // Convert screen direction → isometric tile direction
        const rawTDX = sDX + sDY;
        const rawTDY = -sDX + sDY;
        const tDX = rawTDX > 0 ? 1 : rawTDX < 0 ? -1 : 0;
        const tDY = rawTDY > 0 ? 1 : rawTDY < 0 ? -1 : 0;
        const wasdDir = this.directionFromDelta(tDX, tDY);

        // Store for tile-arrival chaining
        this.wasdMoving = true;
        this.wasdTileDX = tDX;
        this.wasdTileDY = tDY;

        // Cancel click-to-move path when WASD takes over
        if (player.movementQueue && player.movementQueue.length > 1) {
          this.snapMidMovement();
        }

        // Queue the next tile if the player is idle (no pending movement)
        if ((!player.movementQueue || player.movementQueue.length === 0) && player.moveProgress === 0) {
          const targetTX = player.position.tileX + tDX;
          const targetTY = player.position.tileY + tDY;
          const tile = room.tiles[targetTY]?.[targetTX];
          if (tile?.walkable) {
            // Stand up from furniture if seated
            if (player.animState === "device" || player.animState === "device_sit") {
              player.position.elevation = 0;
            }
            // Disengage combat if walking away
            if (player.combatState.active) {
              player.combatState.active = false;
              player.combatState.targetId = null;
              player.combatState.combo = 0;
              player.combatState.strikeQueued = false;
              player.combatState.strikeQueuedAt = 0;
              player.combatState.staggerTimer = 0;
              addToast(this.state, "Fled from combat", "info", 2);
            }
            player.movementQueue = [{ tileX: targetTX, tileY: targetTY }];
            player.targetPosition = { tileX: targetTX, tileY: targetTY };
            if (player.animState !== "walk" && player.animState !== "device_walk") {
              player.animState = this.state.ui.deviceOpen ? "device_walk" as import("./state.js").AnimState : "walk";
            }
            this.requestTurn(wasdDir);
          } else {
            // Blocked tile — just turn to face the direction
            this.requestTurn(wasdDir);
          }
        }
      } else {
        // WASD keys released — stop chaining after current step completes
        this.wasdMoving = false;
      }
    }

    // Click-to-move / NPC interaction / furniture selection
    if (!this.state.ui.uiClickConsumed && !this.state.ui.mapOpen && inputState.clickedTile && this.state.currentRoom) {
      const { tx, ty } = inputState.clickedTile;
      const room = this.state.currentRoom;
      if (tx >= 0 && tx < room.width && ty >= 0 && ty < room.height) {

        // ── Creature click detection — click to engage ──
        const clickedCreature = this.state.creatures.find(c =>
          c.position.tileX === tx && c.position.tileY === ty
        );
        if (clickedCreature) {
          // Always set as target (even if too far for combat)
          targetCreature(this.state, clickedCreature.id);

          // Dead players can't fight
          if (this.state.player.death.isDead) {
            this.state.ui.uiClickConsumed = true;
            return;
          }

          this.snapMidMovement();
          const playerPos = this.state.player.position;
          const dist = Math.abs(tx - playerPos.tileX) + Math.abs(ty - playerPos.tileY);
          if (dist <= 2) {
            // Close enough — start combat immediately
            startCombat(this.state, clickedCreature.id);
          } else {
            // Too far — walk toward the creature, then engage on arrival
            const pathWalkable = (px: number, py: number): boolean => {
              if (px < 0 || px >= room.width || py < 0 || py >= room.height) return false;
              const t = room.tiles[py]?.[px];
              return t ? t.walkable : false;
            };
            const path = findPath(
              pathWalkable,
              { tileX: playerPos.tileX, tileY: playerPos.tileY },
              { tileX: tx, tileY: ty },
              { maxLength: 30, allowDiagonal: true },
            );
            if (path && path.length >= 2) {
              this.state.player.targetPosition = { tileX: tx, tileY: ty };
              this.state.player.movementQueue = path.slice(1);
              this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: true };
              // Combat will auto-engage via proximity aggro when player arrives near creature
            }
          }
          this.state.ui.uiClickConsumed = true;
          return;
        }

        // ── NPC click detection — walk to + interact ──
        const clickedNpc = this.state.npcs.find(n =>
          n.position.tileX === tx && n.position.tileY === ty
        );
        if (clickedNpc) {
          this.snapMidMovement();
          const playerPos = this.state.player.position;
          const npcDist = Math.abs(tx - playerPos.tileX) + Math.abs(ty - playerPos.tileY);
          if (npcDist <= 2) {
            // Close enough — interact immediately
            this.handleNpcInteraction(clickedNpc);
          } else {
            // Walk toward NPC, then interact on arrival
            const pathWalkable = (px: number, py: number): boolean => {
              if (px < 0 || px >= room.width || py < 0 || py >= room.height) return false;
              const t = room.tiles[py]?.[px];
              return t ? t.walkable : false;
            };
            // Find path to an adjacent tile (NPC tile itself may be unwalkable)
            const adjacentTiles = [
              { tileX: tx - 1, tileY: ty }, { tileX: tx + 1, tileY: ty },
              { tileX: tx, tileY: ty - 1 }, { tileX: tx, tileY: ty + 1 },
            ].filter(t => pathWalkable(t.tileX, t.tileY));
            // Pick the closest adjacent tile
            let bestAdj = adjacentTiles[0];
            let bestAdjDist = Infinity;
            for (const adj of adjacentTiles) {
              const d = Math.abs(adj.tileX - playerPos.tileX) + Math.abs(adj.tileY - playerPos.tileY);
              if (d < bestAdjDist) { bestAdjDist = d; bestAdj = adj; }
            }
            if (bestAdj) {
              const path = findPath(
                pathWalkable,
                { tileX: playerPos.tileX, tileY: playerPos.tileY },
                bestAdj,
                { maxLength: 30, allowDiagonal: true },
              );
              if (path && path.length >= 2) {
                this.state.player.targetPosition = bestAdj;
                this.state.player.movementQueue = path.slice(1);
                this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: true };
                // Interaction will trigger via NPC proximity prompt when player arrives
              }
            }
            // Also show dialogue preview as speech bubble regardless of distance
            this.handleNpcInteraction(clickedNpc);
          }
          this.state.ui.uiClickConsumed = true;
          return;
        }

        // ── Screen-space hit region check (priority-ordered, diamond + rect) ──
        // Runs in both edit and normal mode. In edit mode, diamond hits select
        // furniture; in normal mode, only actionable hit regions (interact prompts,
        // toolbar buttons) are processed.
        let hitFurnitureId: string | null = null;
        if (inputState.clickPosition) {
          const { x: cx, y: cy } = inputState.clickPosition;
          const sorted = this.state.ui.hitRegions.slice().sort((a, b) => b.priority - a.priority);
          for (const hr of sorted) {
            let hit = false;
            if (hr.diamonds && hr.diamonds.length > 0) {
              hit = hr.diamonds.some(d => {
                const dx = Math.abs(cx - d.cx) / d.hw;
                const dy = Math.abs(cy - d.cy) / d.hh;
                return dx + dy <= 1.0;
              });
            } else {
              hit = cx >= hr.x && cx <= hr.x + hr.w && cy >= hr.y && cy <= hr.y + hr.h;
            }
            if (hit) {
              if (hr.action) {
                hr.action(this.state);
                // Immediately consume furniture interaction set by the action
                // so there's zero frame lag between click and response
                const pendingId = this.state.ui.pendingInteractFurnitureId;
                if (pendingId && this.interactionSystem) {
                  this.state.ui.pendingInteractFurnitureId = null;
                  const promptResult = this.interactionSystem.interactWithFurniture(this.state, pendingId);
                  if (promptResult?.sound && this.audioEngine) {
                    this.audioEngine.playInteraction(promptResult.sound);
                  }
                }
                this.state.ui.uiClickConsumed = true;
                return;
              }
              // Diamond body hits only matter in edit mode (selection)
              if (this.state.ui.editMode) {
                hitFurnitureId = hr.id;
              }
              break;
            }
          }
        }

        const tile = room.tiles[ty]?.[tx];
        const tileFurniture = tile?.furniture ?? null;
        const furnitureHit = hitFurnitureId ?? tileFurniture;
        if (furnitureHit && this.state.ui.editMode) {
          // ── Edit mode: clicked on furniture tile/sprite → toggle selection ──
          if (this.state.ui.selectedFurniture === furnitureHit) {
            this.state.ui.selectedFurniture = null;
          } else {
            this.state.ui.selectedFurniture = furnitureHit;
            this.state.ui.furnitureContextId = null;
            this.state.ui.uiSounds.push("ui_click");
          }
        } else if (tile?.walkable || (tile?.furniture && !this.state.ui.editMode)) {
          // ── Normal mode: furniture tiles are walkable-through for click-to-move ──
          // In edit mode: only empty walkable tiles trigger movement
          // ── Clicked on walkable tile — deselect furniture & move ──
          this.state.ui.selectedFurniture = null;
          this.state.ui.furnitureContextId = null;

          // Combat + movement: clicking to move during combat disengages combat.
          // The player breaks away from the fight to flee — combat ends, then walk.
          if (this.state.player.combatState.active) {
            this.state.player.combatState.active = false;
            this.state.player.combatState.targetId = null;
            this.state.player.combatState.combo = 0;
            this.state.player.combatState.strikeQueued = false;
            this.state.player.combatState.strikeQueuedAt = 0;
            this.state.player.combatState.staggerTimer = 0;
            this.state.player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";
            addToast(this.state, "Fled from combat", "info", 2);
          }

          // Device + movement interaction:
          //   Phone → walk slowly while holding (device_walk), overlay stays open
          //   Pad → auto-stow (requires both hands, can't walk)
          // Clear furniture sit/sleep state when starting to walk
          if (this.state.player.animState === "sit" || this.state.player.animState === "sleep" ||
              this.state.player.animState === "device_sit") {
            this.state.player.position.elevation = 0;
          }

          // Snap position if mid-step to prevent visual teleportation
          this.snapMidMovement();

          // Compute A* path from current (snapped) position to clicked tile
          const playerPos = this.state.player.position;
          const pathWalkable = (px: number, py: number): boolean => {
            if (px < 0 || px >= room.width || py < 0 || py >= room.height) return false;
            const t = room.tiles[py]?.[px];
            return t ? t.walkable : false;
          };
          const path = findPath(
            pathWalkable,
            { tileX: playerPos.tileX, tileY: playerPos.tileY },
            { tileX: tx, tileY: ty },
            { maxLength: 30, allowDiagonal: true },
          );
          if (!path || path.length < 2) {
            // No path found or already there — try direct single-tile fallback
            if (Math.abs(tx - playerPos.tileX) <= 1 && Math.abs(ty - playerPos.tileY) <= 1) {
              this.state.player.targetPosition = { tileX: tx, tileY: ty };
              this.state.player.movementQueue = [{ tileX: tx, tileY: ty }];
              this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: true };
            } else {
              // Unreachable — flash red X + toast
              this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: false };
              addToast(this.state, "Can't reach there", "info", 1.5);
              return; // can't reach — skip movement
            }
          } else {
            // Skip first entry (current position), queue the rest
            this.state.player.targetPosition = { tileX: tx, tileY: ty };
            this.state.player.movementQueue = path.slice(1);
            this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: true };
          }

          if (this.state.ui.deviceOpen) {
            const dev = this.state.player.device;
            if (dev.activeDevice === "phone") {
              // Walk with phone — slower, distracted amble
              this.state.player.animState = "device_walk" as import("./state.js").AnimState;
              const ddx = tx - playerPos.tileX;
              const ddy = ty - playerPos.tileY;
              this.requestTurn(this.directionFromDelta(ddx, ddy));
              return; // skip normal walk setup
            } else {
              // Pad — stow before walking
              this.deviceOverlay.close(this.state);
              if (this.audioEngine) this.audioEngine.playInteraction("device_close");
            }
          }
          this.state.player.animState = "walk";
          const dx = tx - playerPos.tileX;
          const dy = ty - playerPos.tileY;
          this.requestTurn(this.directionFromDelta(dx, dy));
        } else if (tile) {
          // Blocked tile — deselect furniture + gentle feedback
          this.state.ui.selectedFurniture = null;
          this.state.ui.clickIndicator = { tileX: tx, tileY: ty, time: performance.now(), reachable: false };
          if (this.audioEngine) {
            this.audioEngine.playFootstep("stone"); // soft tap = "nope"
          }
        }
      }
    }

    // ── Interaction auto-reset ──────────────────────────────────────────
    // The interact animation plays for a fixed duration then returns to idle.
    // Without this, interaction actions leave the player frozen in pose.
    const player = this.state.player;
    if (player.animState === "interact") {
      this.interactTimer += dt;
      if (this.interactTimer >= GameLoop.INTERACT_DURATION) {
        player.animState = this.state.ui.combatStance ? "combat_idle" as import("./state.js").AnimState : "idle";
        this.interactTimer = 0;
      }
    } else {
      this.interactTimer = 0;
    }

    // ── Smooth turning — step through intermediate compass directions ──
    if (player.direction !== this.turnTargetDir) {
      // Only animate the turn if the player is actively moving or starting to.
      // If idle with a mismatch (e.g. direction set by zone entry, combat, NPC
      // facing), sync the target to the current direction — don't fight it.
      const isMoving = (player.movementQueue && player.movementQueue.length > 0) ||
                       player.animState === "walk" || player.animState === "device_walk";
      if (!isMoving) {
        this.turnTargetDir = player.direction;
      } else {
        this.turnStepTimer -= dt;
        if (this.turnStepTimer <= 0) {
          const R = GameLoop.DIR_RING;
          const curIdx = R.indexOf(player.direction);
          const tgtIdx = R.indexOf(this.turnTargetDir);
          const diff = ((tgtIdx - curIdx) + 8) % 8;
          // Shortest rotation: CW if diff ≤ 4, else CCW
          const step = diff <= 4 ? 1 : -1;
          player.direction = R[((curIdx + step) % 8 + 8) % 8];
          this.turnStepTimer = GameLoop.TURN_STEP_TIME;
        }
      }
    }

    // Movement interpolation — paused while mid-turn so the character
    // does a natural pivot-on-the-spot before walking in the new direction.
    const isTurning = player.direction !== this.turnTargetDir;

    // Post-turn ease-in: when a multi-step turn just finished, ramp speed
    // from 0→1 so the character doesn't snap to full walk speed.
    if (this.wasTurning && !isTurning) {
      // Turn just completed — start acceleration ramp
      this.postTurnAccel = 0;
    }
    this.wasTurning = isTurning;
    if (this.postTurnAccel < 1) {
      this.postTurnAccel = Math.min(1, this.postTurnAccel + dt / GameLoop.POST_TURN_ACCEL_TIME);
    }

    if (player.movementQueue && player.movementQueue.length > 0 && !isTurning) {
      const target = player.movementQueue[0];
      // 1.8 tiles/sec → 0.55s per tile → 1 gait cycle per tile ≈ 109 BPM cadence.
      // Matches natural human walk rhythm. Legs, arms, bob, and traversal all sync.
      const MOVE_SPEED = 1.8;
      // Walk slower while holding phone — distracted stroll (~80 BPM)
      let speed = player.animState === "device_walk" ? MOVE_SPEED * 0.75 : MOVE_SPEED;
      // Apply post-turn ease-in (smooth cubic ramp)
      const accel = this.postTurnAccel;
      speed *= accel * accel * (3 - 2 * accel); // smoothstep

      // Decelerate on the final tile — natural bodies don't stop instantly.
      // Ease out over the last 40% of the final step for a smooth arrival.
      const isLastTile = player.movementQueue.length === 1;
      if (isLastTile && player.moveProgress > 0.6) {
        const decelT = (player.moveProgress - 0.6) / 0.4; // 0→1 over final 40%
        speed *= 1.0 - decelT * 0.5; // slow to 50% at the end
      }

      player.moveProgress += dt * speed;

      if (player.moveProgress >= 1.0) {
        // Arrived at next tile
        player.position.tileX = target.tileX;
        player.position.tileY = target.tileY;
        // Sync elevation to tile height — player follows terrain
        const arrivalTile = this.state.currentRoom?.tiles[target.tileY]?.[target.tileX];
        if (arrivalTile) player.position.elevation = arrivalTile.elevation;
        player.moveProgress = 0;
        player.movementQueue.shift();

        // Play footstep sound + emit dust particles
        if (this.state.currentRoom) {
          const tile = this.state.currentRoom.tiles[target.tileY]?.[target.tileX];
          if (tile) {
            if (this.audioEngine) {
              this.audioEngine.playFootstep(tile.type);
            }
            this.emitFootstepDust(tile.type, target.tileX, target.tileY, tile.elevation);
          }
        }

        // Check for pending furniture interaction
        if (this.interactionSystem?.hasPendingInteraction()) {
          // Player has arrived at target tile near furniture
          const result = this.interactionSystem.checkPendingArrival(this.state);
          if (result?.sound && this.audioEngine) {
            this.audioEngine.playInteraction(result.sound);
          }
        }

        if (player.movementQueue.length === 0) {
          // ── WASD chaining: if keys are still held, queue the next tile ──
          if (this.wasdMoving && this.state.currentRoom) {
            const nextTX = player.position.tileX + this.wasdTileDX;
            const nextTY = player.position.tileY + this.wasdTileDY;
            const nextTile = this.state.currentRoom.tiles[nextTY]?.[nextTX];
            if (nextTile?.walkable) {
              player.movementQueue = [{ tileX: nextTX, tileY: nextTY }];
              player.targetPosition = { tileX: nextTX, tileY: nextTY };
              const chainDir = this.directionFromDelta(this.wasdTileDX, this.wasdTileDY);
              this.requestTurn(chainDir);
              // Skip the idle transition — stay in walk
            } else {
              // Blocked — stop and return to idle
              player.animState = (this.state.ui.deviceOpen && player.device.animPhase === "held" && player.device.activeDevice === "phone")
                ? "device" as import("./state.js").AnimState
                : this.state.ui.combatStance
                  ? "combat_idle" as import("./state.js").AnimState
                  : "idle";
              player.targetPosition = undefined;
            }
          } else {
            // Return to the appropriate standing pose based on current mode
            player.animState = (this.state.ui.deviceOpen && player.device.animPhase === "held" && player.device.activeDevice === "phone")
              ? "device" as import("./state.js").AnimState
              : this.state.ui.combatStance
                ? "combat_idle" as import("./state.js").AnimState
                : "idle";
            player.targetPosition = undefined;
          }
        } else {
          // Face next tile — use smooth turn for natural path-following
          const next = player.movementQueue[0];
          const dx = next.tileX - player.position.tileX;
          const dy = next.tileY - player.position.tileY;
          const nextDir = this.directionFromDelta(dx, dy);
          // Mid-path turns of ≤1 step apply immediately (no visible hitch)
          // Larger turns (e.g. hairpin switchback) step smoothly
          this.requestTurn(nextDir);
        }
      }
    }
  }

  // ─── Furniture Placement ─────────────────────────────────────────────────────────

  private handlePlacement(tx: number, ty: number): void {
    const { state } = this;
    const room = state.currentRoom;
    const ui   = state.ui;
    if (!room || !ui.placingFurnitureType) return;

    // Compute full footprint for the placement at this anchor tile
    const footprint = getFootprintTiles({
      type: ui.placingFurnitureType,
      tileX: tx,
      tileY: ty,
      direction: ui.placingDirection,
    });

    // Validate ALL footprint tiles are in-bounds and unoccupied
    for (const t of footprint) {
      if (t.x < 0 || t.x >= room.width || t.y < 0 || t.y >= room.height) return;
      const ftile = room.tiles[t.y]?.[t.x];
      if (!ftile || ftile.furniture) return; // out of bounds or occupied
    }

    const tile = room.tiles[ty]?.[tx];
    if (!tile) return;

    // ── MOVE MODE: reposition an existing piece ──
    if (ui.movingFurnitureId) {
      const p = room.furniture.find(f => f.id === ui.movingFurnitureId);
      if (p) {
        p.tileX    = tx;
        p.tileY    = ty;
        p.direction = ui.placingDirection as import("./state.js").Direction;
        p.elevation = tile.elevation;
        // Mark all footprint tiles
        for (const t of footprint) {
          room.tiles[t.y][t.x].furniture = p.id;
          room.tiles[t.y][t.x].walkable  = false;
        }
      }
      ui.movingFurnitureId    = null;
      ui.movingOriginalTile   = null;
      ui.placingFurnitureType = null;
      return;
    }

    // ── NEW PLACEMENT: place from inventory ──
    const id: string = `f_placed_${Date.now()}_${tx}_${ty}`;
    const placement: FurniturePlacement = {
      id,
      type:      ui.placingFurnitureType,
      tileX:     tx,
      tileY:     ty,
      elevation: tile.elevation,
      direction: ui.placingDirection as import("./state.js").Direction,
      state:     {},
    };

    room.furniture.push(placement);
    // Mark all footprint tiles
    for (const t of footprint) {
      room.tiles[t.y][t.x].furniture = id;
      room.tiles[t.y][t.x].walkable  = false;
    }
    // Material-appropriate placement sound
    const placeMat = getFurnitureType(ui.placingFurnitureType!)?.material ?? "wood";
    const placeSound = placeMat === "stone" ? "place_stone"
      : placeMat === "metal" ? "place_metal" : "place_wood";
    ui.uiSounds.push(placeSound);

    // Decrement inventory
    const inv = state.player.inventory;
    const idx = inv.findIndex(
      (it) => it.type === "furniture" && it.furnitureTypeId === ui.placingFurnitureType
    );
    if (idx !== -1) {
      inv[idx].quantity -= 1;
      if (inv[idx].quantity <= 0) inv.splice(idx, 1);
    }

    // Exit placement mode if nothing left to place
    const remaining = inv.find(
      (it) => it.type === "furniture" && it.furnitureTypeId === ui.placingFurnitureType
    );
    if (!remaining) {
      ui.placingFurnitureType = null;
    }
  }

  /** Cancel move mode — restore the furniture to its original tile. */
  private cancelMove(): void {
    const { state } = this;
    const room = state.currentRoom;
    const ui   = state.ui;
    if (!room || !ui.movingFurnitureId) return;

    const p = room.furniture.find(f => f.id === ui.movingFurnitureId);
    if (p && ui.movingOriginalTile) {
      const { tileX, tileY } = ui.movingOriginalTile;
      p.tileX = tileX;
      p.tileY = tileY;
      // Restore all footprint tiles
      const footprint = getFootprintTiles(p);
      for (const t of footprint) {
        const tile = room.tiles[t.y]?.[t.x];
        if (tile) { tile.furniture = p.id; tile.walkable = false; }
      }
    }
    ui.movingFurnitureId    = null;
    ui.movingOriginalTile   = null;
    ui.placingFurnitureType = null;
  }

  /**
   * Handle clicking on an NPC — open full dialog for quest/training NPCs,
   * or cycle simple chat bubbles for regular NPCs.
   */
  private handleNpcInteraction(npc: import("./state.js").NpcState): void {
    if (npc.dialogue.length === 0) return;

    // Quest-relevant NPCs open the full dialog panel
    const dialogRoles = new Set(["quest_giver", "instructor", "arena_master", "dummy_master", "merchant"]);
    if (dialogRoles.has(npc.role) || npc.questMark) {
      npc.inConversation = true;
      openDialog(this.state, npc);

      if (this.audioEngine) {
        this.audioEngine.playInteraction("device_tap");
      }
      return;
    }

    // Simple NPCs: chat bubble behavior
    npc.inConversation = true;
    setTimeout(() => { npc.inConversation = false; }, 6000);

    const line = npc.dialogue[npc.dialogueIndex % npc.dialogue.length];
    npc.dialogueIndex = (npc.dialogueIndex + 1) % npc.dialogue.length;

    // Show as chat bubble
    const bubbleId = `npc_${npc.id}`;
    const now = Date.now();
    this.state.chat.activeBubbles = this.state.chat.activeBubbles.filter(b => b.senderId !== bubbleId);
    this.state.chat.activeBubbles.push({
      senderId: bubbleId,
      text: `${npc.name}: ${line}`,
      until: now + 5000,
    });

    // Also add to chat log
    this.state.chat.messages.push({
      id: `msg_${bubbleId}_${now}`,
      senderId: bubbleId,
      senderName: npc.name,
      text: line,
      timestamp: now,
    });

    // Role-specific interaction sound
    if (this.audioEngine) {
      if (npc.role === "merchant") {
        this.audioEngine.playInteraction("toggle");
      } else {
        this.audioEngine.playInteraction("device_tap");
      }
    }
  }

  /**
   * Right-click at canvas position (x, y) — find the tile and its furniture,
   * then open the context menu positioned near the click.
   */
  private handleContextClick(x: number, y: number): void {
    const { state } = this;
    const room = state.currentRoom;
    if (!room) return;

    // Resolve the tile at the right-click position
    let tx: number, ty: number;
    try {
      const pt = screenToTile(x, y, asCamera(state));
      tx = pt.tx; ty = pt.ty;
    } catch { return; }

    if (tx < 0 || tx >= room.width || ty < 0 || ty >= room.height) return;

    // Close any existing context menu first
    closeContextMenu(state);

    // ── Check for entities at this tile ──────────────────────────────────

    // Creature right-click → context menu
    const clickedCreature = state.creatures.find(c =>
      c.position.tileX === tx && c.position.tileY === ty
    );
    if (clickedCreature) {
      openCreatureContextMenu(state, clickedCreature.id, x, y);
      return;
    }

    // NPC right-click → context menu
    const clickedNpc = state.npcs.find(n =>
      n.position.tileX === tx && n.position.tileY === ty
    );
    if (clickedNpc) {
      openNpcContextMenu(state, clickedNpc.id, x, y);
      return;
    }

    // Other player right-click → context menu
    const clickedPlayer = state.otherPlayers.find(p =>
      p.position.tileX === tx && p.position.tileY === ty
    );
    if (clickedPlayer) {
      openPlayerContextMenu(state, clickedPlayer.id, x, y);
      return;
    }

    const tile = room.tiles[ty]?.[tx];
    if (!tile?.furniture) {
      // Right-click on empty tile → deselect
      state.ui.selectedFurniture = null;
      state.ui.furnitureContextId = null;
      return;
    }

    // Right-click on furniture → enter edit mode and select it
    state.ui.editMode = true;
    state.ui.combatStance = false;
    state.ui.selectedFurniture = tile.furniture;
    state.ui.furnitureContextId = null;
    // Stop movement — player shouldn't walk while entering build mode
    state.player.movementQueue = [];
    state.player.targetPosition = undefined;
  }

  // ─── Context Menu Action Dispatch ──────────────────────────────────────────

  /**
   * Execute the action selected from a right-click context menu.
   * Maps option IDs to their corresponding game system calls.
   */
  private handleContextMenuAction(
    actionId: string,
    targetId: string | null,
    targetType: string,
  ): void {
    const { state } = this;
    if (!targetId) return;

    switch (actionId) {
      // ── Creature actions ──────────────────────────────────────────────
      case "attack": {
        const creature = state.creatures.find(c => c.id === targetId);
        if (creature) {
          targetCreature(state, targetId);
          // Combat system will pick up the targeted creature via aggro scan
          addToast(state, `Engaging ${creature.name}`, "system");
          addCombatLogEntry(`You engage ${creature.name}`, "#e0c080");
        }
        break;
      }
      case "examine": {
        const creature = state.creatures.find(c => c.id === targetId);
        if (creature) {
          targetCreature(state, targetId);
          addToast(state, `Examining ${creature.name}`, "info");
        }
        break;
      }

      // ── NPC actions ───────────────────────────────────────────────────
      case "talk": {
        const npc = state.npcs.find(n => n.id === targetId);
        if (npc) {
          targetNpc(state, targetId);
          addToast(state, `Speaking with ${npc.name}`, "info");
        }
        break;
      }
      case "trade_npc": {
        addToast(state, "Trade is not yet available", "system");
        break;
      }
      case "quest": {
        addToast(state, "Quests coming soon", "system");
        break;
      }

      // ── Player actions ────────────────────────────────────────────────
      case "duel": {
        const msg = challengeToDuel(state, targetId);
        addToast(state, msg, "pvp");
        addCombatLogEntry(msg, "#e88020");
        if (this.audioEngine) this.audioEngine.playInteraction("duel_request");
        break;
      }
      case "party_invite": {
        const other = state.otherPlayers.find(p => p.id === targetId);
        if (other) {
          addToast(state, `Party invite sent to ${other.name}`, "system");
        }
        break;
      }
      case "set_focus": {
        if (targetType === "creature") {
          const c = state.creatures.find(cr => cr.id === targetId);
          if (c) {
            state.player.focusTarget.id = targetId;
            state.player.focusTarget.type = "creature";
            state.player.focusTarget.name = c.name;
            state.player.focusTarget.level = 1;
            state.player.focusTarget.disposition = "hostile";
            state.player.focusTarget.setAt = performance.now() / 1000;
            addToast(state, `Focus: ${c.name}`, "system");
          }
        } else if (targetType === "player") {
          const p = state.otherPlayers.find(pl => pl.id === targetId);
          if (p) {
            state.player.focusTarget.id = targetId;
            state.player.focusTarget.type = "player";
            state.player.focusTarget.name = p.name;
            state.player.focusTarget.level = p.combat.level;
            state.player.focusTarget.disposition = p.pvpAggressor ? "pvp_aggressor" : p.pvpFlagged ? "pvp_flagged" : "friendly";
            state.player.focusTarget.setAt = performance.now() / 1000;
            addToast(state, `Focus: ${p.name}`, "system");
          }
        }
        break;
      }
      case "mark_skull":
      case "mark_cross":
      case "mark_star":
      case "mark_diamond":
      case "mark_moon":
      case "mark_clear": {
        const markerMap: Record<string, import("../engine/state.js").TargetMarker> = {
          mark_skull: "skull", mark_cross: "cross", mark_star: "star",
          mark_diamond: "diamond", mark_moon: "moon", mark_clear: null,
        };
        if (state.player.target.id === targetId) {
          state.player.target.marker = markerMap[actionId] ?? null;
        }
        break;
      }
      case "trade": {
        addToast(state, "Trade is not yet available", "system");
        break;
      }
      case "whisper": {
        addToast(state, "Whisper coming soon", "system");
        break;
      }
      case "inspect": {
        addToast(state, "Inspecting player gear...", "system");
        break;
      }
      case "follow": {
        addToast(state, "Follow coming soon", "system");
        break;
      }
      case "pvp_attack": {
        addToast(state, "Engaging in PvP combat!", "pvp");
        break;
      }

      // ── NPC actions (extended) ──────────────────────────────────────
      case "spar": {
        // NPC training duel — safe-zone practice combat
        const npc = state.npcs.find(n => n.id === targetId);
        if (npc) {
          state.player.duel.active = true;
          state.player.duel.opponentId = targetId;
          state.player.duel.opponentName = npc.name;
          state.player.duel.countdown = 3;
          state.player.duel.countdownStart = performance.now() / 1000;
          state.player.duel.startedAt = 0;
          state.player.duel.winnerId = null;
          state.player.duel.lastCountdownTick = 4;
          addToast(state, `Sparring match with ${npc.name}!`, "pvp");
          addCombatLogEntry(`Training duel with ${npc.name} begins!`, "#e88020");
          if (this.audioEngine) this.audioEngine.playInteraction("duel_request");
        }
        break;
      }
      case "report": {
        addToast(state, "Report submitted", "system");
        break;
      }

      default:
        break;
    }

    closeContextMenu(state);
  }

  // ─── Footstep Dust Particles ────────────────────────────────────────────

  /**
   * Emit subtle dust particles at the player's feet based on tile type.
   * These are barely-there — you notice them subconsciously, not consciously.
   *
   * - Dirt/sand: warm tan puffs (3–4 particles)
   * - Stone/cobblestone: tiny grey specks (1–2 particles)
   * - Grass/wood: nothing (soft/solid surfaces)
   * - Water: small blue-white splash (2–3 particles)
   */
  private emitFootstepDust(
    tileType: string,
    tileX: number,
    tileY: number,
    elevation: number,
  ): void {
    let count: number;
    let r: number, g: number, b: number;
    let alpha: number;
    let life: number;
    let speed: number;

    switch (tileType) {
      case "dirt":
        count = 3 + Math.floor(Math.random() * 2); // 3–4
        r = 180; g = 155; b = 120;  // warm tan
        alpha = 0.2;
        life = 0.4;
        speed = 12;
        break;
      case "sand":
        count = 3 + Math.floor(Math.random() * 2); // 3–4
        r = 210; g = 190; b = 140;  // pale sand
        alpha = 0.18;
        life = 0.5;
        speed = 10;
        break;
      case "stone":
      case "stone_dark":
      case "cobblestone":
      case "marble":
        count = 1 + Math.floor(Math.random() * 2); // 1–2
        r = 140; g = 140; b = 145;  // grey
        alpha = 0.12;
        life = 0.3;
        speed = 8;
        break;
      case "water":
        count = 2 + Math.floor(Math.random() * 2); // 2–3
        r = 200; g = 220; b = 255;  // blue-white
        alpha = 0.25;
        life = 0.35;
        speed = 15;
        break;
      case "snow":
        count = 2 + Math.floor(Math.random() * 2); // 2–3
        r = 230; g = 235; b = 250;  // cool white
        alpha = 0.15;
        life = 0.5;
        speed = 6;
        break;
      case "lava":
        count = 2;
        r = 255; g = 140; b = 40;   // ember orange
        alpha = 0.35;
        life = 0.3;
        speed = 18;
        break;
      default:
        // Grass, wood, carpet, crystal, dock — no dust
        return;
    }

    // Project the foot position to screen space
    const cam = { x: this.state.camera.x, y: this.state.camera.y, zoom: this.state.camera.zoom };
    const ws = worldToScreen(tileX + 0.5, tileY + 0.5, elevation);
    const sc = applyCamera(ws, cam);

    // Emit particles at the player's feet
    const particles = this.state.footstepParticles;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const spd = speed * (0.5 + Math.random() * 0.5);
      const sz = 1 + Math.random();                    // 1–2 px

      particles.push({
        x: sc.x + (Math.random() - 0.5) * 6,
        y: sc.y + (Math.random() - 0.5) * 3,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd * 0.5 - spd * 0.7,  // bias upward
        life,
        maxLife: life,
        size: sz,
        r: r + Math.floor((Math.random() - 0.5) * 20),
        g: g + Math.floor((Math.random() - 0.5) * 20),
        b: b + Math.floor((Math.random() - 0.5) * 20),
        alpha: alpha * (0.7 + Math.random() * 0.3),
      });
    }
  }

  /**
   * If the player is mid-step when a new destination is clicked, snap the
   * logical position to prevent visual teleportation.
   *
   * moveProgress > 0.5 → complete the step (snap forward to movement target)
   * moveProgress ≤ 0.5 → abort the step (stay at origin tile)
   *
   * Always resets moveProgress to 0 and clears the old queue so the new
   * path can be calculated from the corrected position.
   */
  private snapMidMovement(): void {
    const player = this.state.player;
    if (player.moveProgress > 0 && player.movementQueue && player.movementQueue.length > 0) {
      const target = player.movementQueue[0];
      if (player.moveProgress > 0.5) {
        // More than halfway — complete the step
        player.position.tileX = target.tileX;
        player.position.tileY = target.tileY;
        const arrivalTile = this.state.currentRoom?.tiles[target.tileY]?.[target.tileX];
        if (arrivalTile) player.position.elevation = arrivalTile.elevation;
      }
      // Else: less than halfway — stay at origin tile (position already correct)
      player.moveProgress = 0;
      player.movementQueue = [];
    }
  }

  /**
   * Request a direction change that will be applied smoothly, stepping
   * through intermediate compass points over multiple frames.
   * If the target is only 1 step away, it applies immediately (no visible lag).
   */
  private requestTurn(dir: import("./state.js").Direction): void {
    if (dir === this.turnTargetDir && dir === this.state.player.direction) return;
    this.turnTargetDir = dir;
    // 0-1 step difference: apply immediately for responsiveness
    const R = GameLoop.DIR_RING;
    const curIdx = R.indexOf(this.state.player.direction);
    const tgtIdx = R.indexOf(dir);
    const diff = ((tgtIdx - curIdx) + 8) % 8;
    const dist = Math.min(diff, 8 - diff);
    if (dist <= 1) {
      this.state.player.direction = dir;
    } else {
      // Multi-step turn: kick off the stepping timer
      this.turnStepTimer = 0; // step immediately on next frame
    }
  }

  private directionFromDelta(dx: number, dy: number): import("./state.js").Direction {
    if (dx > 0 && dy === 0) return "SE";
    if (dx < 0 && dy === 0) return "NW";
    if (dx === 0 && dy > 0) return "SW";
    if (dx === 0 && dy < 0) return "NE";
    if (dx > 0 && dy > 0) return "S";
    if (dx < 0 && dy < 0) return "N";
    if (dx > 0 && dy < 0) return "E";
    if (dx < 0 && dy > 0) return "W";
    return "S";
  }

  // ─── Render Dispatch ─────────────────────────────────────────────────────

  private render(timestamp: number): void {
    const ctx = this.ctx;
    const { canvasWidth: W, canvasHeight: H } = this.state;

    ctx.clearRect(0, 0, W, H);

    switch (this.state.scene) {
      case "title":
        this.renderTitle(timestamp);
        break;
      case "room":
        this.renderRoom(timestamp);
        break;
      case "character_create":
        this.renderCharacterCreate(timestamp);
        break;
      case "zone_travel":
        // Draw the transition renderer full-screen
        this.transitionRenderer.draw(this.ctx, W, H, this.state);
        break;
      case "transition":
        // Draw the previous scene, then fade to black
        if (this.state.previousScene === "title") {
          this.renderTitle(timestamp);
        } else if (this.state.previousScene === "room") {
          this.renderRoom(timestamp);
        } else if (this.state.previousScene === "character_create") {
          this.renderCharacterCreate(timestamp);
        }
        // Fade overlay
        const alpha = Math.min(1, this.state.transitionProgress);
        ctx.fillStyle = `rgba(10, 10, 20, ${alpha})`;
        ctx.fillRect(0, 0, W, H);
        break;
      case "loading":
        ctx.fillStyle = "#1a1a2e";
        ctx.fillRect(0, 0, W, H);
        break;
    }
  }

  private renderTitle(timestamp: number): void {
    if (!this.titleRenderer) {
      this.titleRenderer = new TitleRenderer(this.canvas, this.ctx);
    }
    const quality = this.perf.qualityLevel;
    this.titleRenderer.render(timestamp, this.state, quality);
  }

  private renderCharacterCreate(timestamp: number): void {
    if (!this.charCreateRenderer) {
      this.charCreateRenderer = new CharacterCreateRenderer(this.canvas, this.ctx);
    }
    const quality = this.perf.qualityLevel;
    this.charCreateRenderer.render(timestamp, this.state, quality);
  }

  private renderRoom(timestamp: number): void {
    if (!this.roomRenderer) {
      this.roomRenderer = new RoomRenderer(this.canvas, this.ctx);
    }
    const quality = this.perf.qualityLevel;
    this.roomRenderer.mouseX = this.input.state.pointer.x;
    this.roomRenderer.mouseY = this.input.state.pointer.y;
    this.roomRenderer.render(timestamp, this.state, quality);

    // Render particles on top of the room (after room renderer, before any overlay UI)
    if (this.particleSystem) {
      const cam: Camera = { x: this.state.camera.x, y: this.state.camera.y, zoom: this.state.camera.zoom };
      this.particleSystem.render(this.ctx, cam, quality);
    }

    // Combat UI overlay — health bars, damage numbers, status
    {
      const cam: Camera = { x: this.state.camera.x, y: this.state.camera.y, zoom: this.state.camera.zoom };
      const mx = this.input.state.pointer.x;
      const my = this.input.state.pointer.y;
      drawCombatUI(this.ctx, this.state, cam, timestamp, mx, my);

      // Nameplates, target frame, PvP badge, death overlay
      drawNameplatesAndStatus(this.ctx, this.state, cam, timestamp);

      // Skill bar + EP bar + buff strip (visible in danger zones, training zones, or combat)
      const zone = getZone(this.state.world, this.state.world.currentZoneId);
      if ((zone && (zone.dangerLevel > 0 || zone.type === "training")) || this.state.player.combatState.active || this.state.ui.combatStance) {
        drawSkillBar(this.ctx, this.state, timestamp, mx, my);
      }

      // Floating emotes (always rendered if present)
      drawFloatingEmotes(this.ctx, this.state, cam.x, cam.y, cam.zoom, timestamp);
    }

    // Quest UI overlays — tracker, dialog, quest log, quest overlay, DPS meter
    drawQuestTracker(this.ctx, this.state);
    drawDialogPanel(this.ctx, this.state, timestamp);
    drawDPSMeter(this.ctx, this.state, timestamp);
    drawQuestLog(this.ctx, this.state, timestamp);
    drawQuestOverlay(this.ctx, this.state, timestamp);

    // Emote wheel overlay (on top of most things)
    if (this.state.player.emoteWheel.open) {
      drawEmoteWheel(this.ctx, this.state, timestamp, this.input.state.pointer.x, this.input.state.pointer.y);
    }

    // Device overlay — drawn on top of everything (after HUD, after particles)
    if (this.state.ui.deviceOpen || this.state.player.device.animPhase !== "stowed") {
      this.deviceOverlay.draw(this.ctx, this.state, this.state.canvasWidth, this.state.canvasHeight);
    }
  }

  // ─── Public Metrics ───────────────────────────────────────────────────────

  get metrics(): PerformanceMetrics {
    return this.perf.toMetrics();
  }
}

// ─── Tab Visibility ───────────────────────────────────────────────────────────

/**
 * Register the Page Visibility API listener.
 * When the tab hides, we mark the state so the loop knows on next tick.
 * When it shows, we let the loop discover that naturally (dt cap handles it).
 *
 * Called once from main.ts during boot.
 */
export function registerVisibilityHandler(state: GameState): void {
  document.addEventListener("visibilitychange", () => {
    state.tabVisible = !document.hidden;
    if (document.hidden) {
      // Record when we went hidden so we can calculate total time away
      (state as unknown as Record<string, unknown>)._hiddenAt = Date.now();
    }
  });
}
